import * as idb from '../lib/idbd';
import {cast} from '../entities/schemas';
import locals from '../lib/localStorage';
import {captureRequestSession, requestServerCredential, type RequestSession} from '../Auth/sessionRequest';
import {makeSecureApiCall as makeSecureRequest} from './secureApiCall';
import {SessionChangedError, type LoginCandidate} from '../Auth/tabSession';
import {tabSession} from '../Auth/session';
import ls from '../lib/localStorage';
import SOCKET_OPTIONS from '../lib/socket-variables';
import IDP_CONFIG from '../Auth/idp-config';
import store from '../store/store';
import * as StatusBarActions from '../store/actions/statusBarActions';
import * as MiscActions from '../store/actions/miscActions';
import Document from "../entities/document";
import {refreshDocumentSignatures} from '../Documents/refreshSignatures';
import {uploadSizeRefusal} from '../Documents/uploadLimits';
import * as Hooks from '../store/hooks';
import newHRTimestamp from "../lib/picos/timestamp";
import util from "../lib/util";
import {captureException} from "../lib/picos/error";
import {getZeroUUID} from "../lib/picos/uuids";
import {getCurrentFirmId, getCurrentUserId} from "../lib/picos/currentLogin";
import Alert from "../lib/alert";
import * as info from '../../package.json';
import fetchFileWithProgress from '../lib/fetchFileWithProgress';
import find from "../store/find";
import {isDemo} from "../lib/demo";
import pWaitFor from "p-wait-for";
import ENTITIES from "../store/actions";
import {getReasonPhrase, StatusCodes} from "http-status-codes";
import {EntityMapper, EntityType, uuid} from "../@types/entity";
import Firm from '../entities/firm';
import {RETAINED_DELETED_TYPES} from '../lib/retainedDeletedTypes';
import User from "../entities/user";
import Contact from "../entities/contact";
import Role from "../entities/role";
import Address from "../entities/address";
import Resource from "../entities/resource";
import Question from "../entities/question";
import {GetUsers200ResponseOneOfInner} from "../@types/auth0";
import {EntityNames} from "../entities";
import {Err, Ok, Result} from "oxide.ts";
import {EmailOptions} from "../@types/email";
import {HttpError, httpErrorMessage} from "../lib/picos/http";
import {isRecoverableError} from '../lib/recoverableErrors';
import type Task from '../entities/task';
import {acknowledgeTaskSave, lineageOf, refuseTaskSave, staleConflictOf, taskWire} from './staleTaskSaves';
import {socket} from "../lib/picos/socket";
import Stripe from "stripe";
import {globalAbortSignal} from "../lib/picos/abortController";
import Auth from "@/Auth/Auth";

type LastPartial = { name: string, maxDbAddedOn: number };

const HISTORICAL_DOCUMENT_MESSAGE = 'A newer document version is current. Open this rejected upload from Needs Attention to recover its content.';
const DELETED_DOCUMENT_MESSAGE = 'This document was deleted. Its unsaved content is preserved here; restore the document before correcting it, or confirm Discard to remove this retained upload.';

export type Auth0User = {
    user_id: string,
    user_name: string,
    email: string,
    firstName: string,
    lastName: string,
    firmName: string,
    firms: string,
};

export type ChangeSubscriptionResult = {
    total: number,
    subtotal: number,
    tax: number,
    invoiceDate: number
};

export type StripeInvoiceSummary = {
    id: string;
    number: string | null;
    status: string;
    amount_due: number;
    amount_paid: number;
    currency: string;
    created: number;
    period_start: number;
    period_end: number;
    invoice_pdf: string | null;
    hosted_invoice_url: string | null;
};

export type UpcomingInvoicePreview = {
    amount_due: number;
    subtotal: number;
    total: number;
    currency: string;
    next_payment_attempt: number | null;
    period_start: number;
    period_end: number;
    tax: number;
    lines: Array<{
        description: string | null;
        amount: number;
        quantity: number | null;
        period: { start: number; end: number };
    }>;
};

export type HttpStatusResult = {
    status: number,
    body: any
}

export type VersionInfo = {
    client: string,
    server: string,
    // OWA-614 — server build identity; optional so older servers (which omit them) still parse.
    serverCommit?: string,
    serverCommitLong?: string,
    serverTag?: string,
    serverBranch?: string,
    serverBuiltAt?: string,
};

export type SendEmailsResult = {
    wasEmailProcessed: "Yes" | "No" | "Maybe",
    messageId?: string | null,
    errorMessage?: string | null,
};

// OWA-380 #7: was {firm, user, contact, role: Role, authResponse: any}.
// `role` should have been `roles: Role[]` and `authResponse` should
// have been `auth`. The type is unused on the success path (callers
// only check `signupResult.isErr()`), but keeping the lie around
// invited future confusion. Aligned with what the server actually
// sends since OWA-379.
type SignupResult = {
    firm: Firm,
    user: User,
    contact: Contact,
    firmContact: Contact | null,
    address: Address | null,
    email: Resource | null,
    roles: Role[],
    auth: any,
    stripe: any,
};

export type VerifyLoginResult = {
    status: number,
    body: {
        firm: Firm,
        user: User,
        contact: Contact,
        subscription?: {
            isFullyCancelled: boolean,
            willCancelAtPeriodEnd: boolean,
            subscriptionEndDate: Date
        }
    }
}

const logFormat = ""; //"color: black; padding: 2px; border-radius: 4px;"
const logFormatErr = ""; //"color: maroon; padding: 2px; border-radius: 4px;"

/**
 * In demo mode, just set the status to 'D' and update fields.
 *
 * @param {EntityType} item
 * @returns {HttpStatusResult}
 */
function fakeDelete(item: EntityType): { body: EntityType; status: number; } {
    const _item = item.clone();

    _item.status = 'D';
    _item.dbAddedOn = newHRTimestamp();
    _item.fresh = false;
    _item.waitingToSave = false;

    return {status: 200, body: _item};
}

/**
 * In demo mode, just set the status to 'U' and update fields.
 *
 * @param {EntityType} item
 * @returns {HttpStatusResult}
 */
function fakeRestore(item: EntityType): { body: EntityType; status: number; } {
    const _item = item.clone();
    _item.status = 'U';
    _item.dbAddedOn = newHRTimestamp();
    _item.persisted = true;
    _item.fresh = false;
    _item.waitingToSave = false;

    return {status: 200, body: _item};
}

/**
 * In demo mode, we don't save to the database, so we just update some of the defaults here.
 *
 * @param {EntityType} item
 * @returns {{status: number, body: EntityType}}
 */
function fakeUpdate(item: EntityType): { status: number; body: EntityType; } {
    const _item = item.clone();

    _item.persisted = true;
    _item.dbAddedOn = _item.addedOn;
    if (!_item.createdOn) _item.createdOn = new Date();
    if (!_item.createdBy) _item.createdBy = _item.addedBy;
    _item.fresh = false;
    _item.waitingToSave = false;

    return {status: 200, body: _item};
}

/**
 * In demo mode, we don't save to the database, so we just update some of the defaults here.
 *
 * @param {EntityType[]} items
 * @returns {{status: number, body: EntityType[]}}
 */
function fakeUpdates(items: EntityType[]): { status: number; body: EntityType[]; } {
    const result = items.map(_item => {
        const item = _item.clone();
        item.persisted = true;
        item.dbAddedOn = item.addedOn;
        if (!item.createdOn) item.createdOn = new Date();
        if (!item.createdBy) item.createdBy = item.addedBy;
        item.fresh = false;
        item.waitingToSave = false;
        return item;
    });

    return {status: 200, body: result};
}

/**
 * The URI of the API - for development builds,
 * use http://localhost:80, otherwise use https://outlawpractice.com
 */
export const URI = `${SOCKET_OPTIONS.secure ? 'https' : 'http'}://${SOCKET_OPTIONS.hostname}`;

/**
 * General debug logging.
 */
let logging = ls.getBoolean(ls.API_LOGGING, false);

/**
 * We need to ensure that only the latest value for any item is saved.
 *
 * @param {EntityType} acc
 * @param {EntityType} curr
 * @returns {EntityType}
 */
const latestItem = (acc: EntityType, curr: EntityType): EntityType => {
    if (acc && ((acc.addedOn ?? 0) > (curr.addedOn ?? 0))) return acc;
    else return curr;
};

/**
 * Remove (DELETE) an item
 *
 * @param {EntityMapper<<T>>} entity - The name of the entity (and queue)
 * @param {string} entity.name - name of the entity
 * @param {function} entity.removeSuccess - redux action to call on success
 * @param {function} entity.removeFailure - redux action to call on failure
 * @param {function} entity.fetchOneQueue - redux action to re-fetch this item
 * @param {<T>} item - The entity to remove
 * @returns {Promise<<T>>} - The entity as passed back from the database (marked deleted)
 */
export async function remove<T extends EntityType>(entity: EntityMapper<T>, item: T, signal?: AbortSignal): Promise<HttpStatusResult | T | undefined> {
    const operation = captureRequestSession();
    const boundApiCall = makeSessionApiCaller(operation);
    // TODO: The return types for this are terrible.  Which is it, a status/error or an entity?
    try {
        if (logging) console.debug(`%c🌐 API: remove for ${entity.name} ${item.id}`, logFormat);

        // Get hooks for this type
        const {before, after} = Hooks.getHooks(entity.name, 'remove');

        // Do any 'before' hooks
        for (let i = 0; i < before.length; i++) {
            const hook = before[i];
                operation.assertCurrent();
            if (hook.async) item = await Hooks[hook.method](item, operation);
            else item = Hooks[hook.method](item, operation);
        }

        // Bind the reply and retirement to the exact operation sent, even if
        // a caller or hook later changes this mutable entity object.
        const deleteOperation = {
            entityName: entity.name, firmId: item.firmId, id: item.id, addedOn: item.addedOn,
        };

        const result = isDemo()
                       ? fakeDelete(item)
                       : await boundApiCall(
                `/${entity.name}/${item.id}`,
                'DELETE',
                {addedOn: item.addedOn, addedBy: item.addedBy, importSource: item.importSource, importId: item.importId},
                signal,
            );

        if (!result) {
            console.error(`%c🌐 API: Error calling server, fetch returned a null for URL /${entity.name}/${item.id}`, logFormatErr);

            const message = store.getState().misc.isConnected
                            ? `Error calling server, returned a null for URL /${entity.name}/${item.id}`
                            : `You are not connected to the server. Please try again later.`;

            return {status: 503, body: message};
        }

        const {status, body} = result;

        // Old servers can report a standby miss as 404/410. Only a versioned,
        // exact-key acknowledgement from a strict primary read proves absence.
        const acknowledgement = body?.deleteAcknowledgement;
        const authoritativeAbsence = (status === 404 || status === 410)
            && typeof acknowledgement === 'object' && acknowledgement !== null && !Array.isArray(acknowledgement)
            && acknowledgement.version === 1 && acknowledgement.outcome === 'absent'
            && typeof deleteOperation.firmId === 'string' && deleteOperation.firmId.length > 0
            && typeof deleteOperation.id === 'string' && deleteOperation.id.length > 0
            && Number.isFinite(deleteOperation.addedOn)
            && acknowledgement.entityName === deleteOperation.entityName
            && acknowledgement.firmId === deleteOperation.firmId
            && acknowledgement.id === deleteOperation.id
            && acknowledgement.addedOn === deleteOperation.addedOn;
        // Preserve the existing 422 policy; every other error retains intent.
        const isUnrecoverable = status === 422 || authoritativeAbsence;

        // Handle HTTP error
        if (status >= 400) {
            // Handle HTTP error
            console.warn(`🌐 API: Error saving ${entity.name}`, status);

            // If unrecoverable error, remove from queue and alert user
            if (isUnrecoverable) {
                const removeResult = await removeForSession(operation, deleteOperation.entityName, deleteOperation.addedOn);
                if (removeResult.isErr()) console.warn(removeResult.unwrapErr());
            }

            // Tell redux that we failed. Handle a bare-string body like save() does (OWA-685).
            const removeMessage = typeof body === 'string' ? body : (body?.clientMessage || body?.message);
            dispatchCurrent(operation, entity.removeFailure(
                item,
                status,
                removeMessage
            ));

            Alert.add(new Alert(Alert.ERROR, item.id, item.addedOn, removeMessage));

            // An acknowledged absence has nothing to refetch. Keep the existing
            // validation-failure refresh and the established failure/alert behavior.
            if (isUnrecoverable && !authoritativeAbsence) {
                dispatchCurrent(operation, entity.fetchOneQueue(item.id, item.addedOn));
            }
        }
        else {
            // Call any 'after' hooks
            for (let i = 0; i < after.length; i++) {
                const hook = after[i];
                operation.assertCurrent();
                if (hook.async) item = await Hooks[hook.method](item, operation);
                else item = Hooks[hook.method](item, operation);
            }

            // OK to retire the acknowledged operation now — and ONLY it. The
            // removeByEntityId fallback that used to follow wiped every queued
            // operation for this id, destroying newer sibling edits enqueued
            // while this call was in flight (OWA-786, CXR-4-004/CU-5-E).
            const removeResult = await removeForSession(operation, deleteOperation.entityName, deleteOperation.addedOn);
            if (removeResult.isErr()) console.warn(removeResult.unwrapErr());

            // Tell redux that we succeeded
            dispatchCurrent(operation, entity.removeSuccess(item));

            // Return the newly deleted object (only change is the timestamp and status)
            const data: EntityType = cast<T>(entity.name, body);

            // Saved, so set the persisted flag to true
            data.persisted = true;

            return data as T;
        }
    }
    catch (err) {
        operation.assertCurrent();
        // This could be an error outside of HTTP
        console.error(`%c🌐 API: Error calling DEL on ${entity.name}`, logFormatErr, err);

        Alert.add(new Alert(Alert.ERROR, item.id, item.addedOn, err.clientMessage || err.message));

        // Tell redux that we failed.  Use a standard 500 error message if one is not given
        dispatchCurrent(operation, entity.removeFailure(item, err.status || 500, err.clientMessage || err.message));

        // Unrecoverable, fetch the old value from the database and refresh
        dispatchCurrent(operation, entity.fetchOneQueue(item.id));

        // Throw the error to the caller
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Deletes a specific version of an entity (usually the latest version)
 *
 * @param {string} entityName - e.g., Task
 * @param {{T}} entity - the specific entity version to delete
 */
export async function deleteVersion<T extends EntityType>(entityName: string, entity: T): Promise<Result<T, HttpError>> {
    // The server records the removal by inserting a refreshed copy of the penultimate
    // version; attribute that new row to the user who clicked Remove Version.
    entity.addedBy = getCurrentUserId();

    const result = await makeSecureApiCall(`/${entityName}/versions/delete`, 'PATCH', entity);
    if (!result) return Err(new HttpError(503, 'Error calling server, try later'));
    else if (result.status >= 400) {
        // Handle a bare-string body like save() does (OWA-685).
        return Err(new HttpError(result.status, httpErrorMessage(result.status, result.body)));
    }
    else return Ok(cast<T>(entityName, result.body));
}

/**
 * Restores a specific version of an entity (usually the latest version)
 *
 * @param {string} entityName - e.g. Task
 * @param {{T}} entity - the ID of the entity to restore
 */
export async function restoreVersion<T extends EntityType>(entityName: string, entity: T): Promise<Result<T, HttpError>> {
    // Update the addedBy so the restored version has the current user as the addedBy
    entity.addedBy = getCurrentUserId();

    const result = await makeSecureApiCall(`/${entityName}/versions/restore`, 'PATCH', entity);
    if (!result) return Err(new HttpError(503, 'Error calling server, try later'));
    else if (result.status >= 400) {
        // Handle a bare-string body like save() does (OWA-685).
        return Err(new HttpError(result.status, httpErrorMessage(result.status, result.body)));
    }
    else return Ok(cast<T>(entityName, result.body));
}

/**
 * Save (POST) an item
 *
 * @param {EntityMapper<<T>>} entity - The name of the entity (and queue)
 * @param {string} entity.name - name of the entity
 * @param {function} entity.saveSuccess - redux action to call on success
 * @param {function} entity.saveFailure - redux action to call on failure
 * @param {function} entity.fetchOneQueue - redux action to re-fetch this item
 * @param {<T>} item - The item to save
 * @returns {Promise<<T>>}
 */
export async function save<T extends EntityType>(entity: EntityMapper<T>, item: T, signal?: AbortSignal): Promise<T | undefined> {
    const operation = captureRequestSession();
    const boundApiCall = makeSessionApiCaller(operation);
    if (!item) throw new Error('🌐 API: Nothing passed to save.');

    const prevAdded = item.dbAddedOn ? `. Last saved to database at ${util.hrtToDate(item.dbAddedOn)}` : '';
    if (logging) console.debug(`🌐 API: save ${entity.name} with ID ${item.id} added at ${util.hrtToDate(item.addedOn ?? 0)}${prevAdded}`);

    // Sanity checks
    if (!entity) throw new Error('🌐 API: save() called with a null entity type');
    if (!item) throw new Error('🌐 API: save() called with a null item');

    if (!store.getState().misc.isConnected) return;

    const queuedItem = {...item, id: item.id} as T;
    // OWA-998: a task save's lineage, read before any hook can replace the object
    const lineage = entity.name === 'task' ? lineageOf(item) : null;

    try {
        if (logging) console.debug(`%c🌐 API: save for ${entity.name}`, logFormat);

        // Get hooks for this type. getHooks is always returns values, no check needed
        const {before, after} = Hooks.getHooks(entity.name, 'save');

        // Do any 'before' hooks. Note that addedOn must not change!

        // Sanity check
        if (!item.addedOn) {
            item.addedOn = newHRTimestamp();
            console.warn('🌐 API: save() called on an entity with no addedOn', item);
        }

        if (!item.addedBy) {
            item.addedBy = getCurrentUserId();
            console.warn('🌐 API: save() called on an entity with no addedBy', item);
        }

        const addedOn = item.addedOn;

        // Call each hook (guaranteed an array by getHooks)
        for (let i = 0; i < before.length; i++) {
            const hook = before[i];
                operation.assertCurrent();

            if (hook.async) item = await Hooks[hook.method](item, operation);
            else item = Hooks[hook.method](item, operation);

            if (!item) {
                console.error(`%c🌐 API: Before hook ${hook.method} returned ${item}, stopping save`, logFormatErr);
                throw new Error('🌐 API: Before hook returned null');
            }
        }

        if ((item.addedOn ?? 0) > addedOn) item.addedOn = addedOn;

        // Do the actual save
        const fetchResult = isDemo()
                            ? fakeUpdate(item)
                            : await boundApiCall(`/${entity.name}`, 'POST', lineage ? taskWire(item) : item, signal);

        // Handle error caused by a null result (like when server is not available)
        if (!fetchResult) throw {status: 503, message: 'Server is not available'};

        const {status, body} = fetchResult;

        // OWA-998: a task save refused as stale stays queued for the user to resolve; it is not
        // an error on the task (no fatalError), and nothing is refetched over it
        const staleConflict = lineage ? staleConflictOf(status, body) : null;
        if (staleConflict) {
            await refuseTaskSave(operation, action => dispatchCurrent(operation, action), item as unknown as Task, lineage!, staleConflict);
            return undefined;
        }

        // A rejected document must remain in the durable queue: it may contain
        // the only copy of an unsaved note or attachment. Other entities retain
        // their existing terminal-4xx policy.
        const isUnrecoverable =
            entity.name !== 'document' && [400, 403, 404, 405, 406, 409, 411, 413, 414, 415, 416, 422].includes(status);

        // Handle HTTP error
        if (status >= 400) {
            console.error('%c🌐 API: Error saving entity', logFormatErr, status, body);

            // If unrecoverable error, remove from queue and alert user
            if (isUnrecoverable) {
                const removeResult = await removeForSession(operation, entity.name, item.addedOn);
                if (removeResult.isErr()) console.warn(removeResult.unwrapErr());
            }

            // Tell redux that we failed
            await reportSaveFailure(operation, entity,
                item,
                status,
                (typeof body === 'string' && body) || body?.clientMessage || body?.message || `Request failed (HTTP ${status})`
            );

            // If unrecoverable, fetch the old value from the database and refresh
            if (isUnrecoverable) dispatchCurrent(operation, entity.fetchOneQueue(item.id, item.addedOn));
        }
        else {
            // Handle normal processing
            if (logging) console.debug(`%c🌐 API: HTTP ${status}: ${entity.name}.save: ${body.clientMessage}`, logFormat);

            let data: EntityType = cast<T>(entity.name, body);

            // Do any 'after' hooks
            for (let i = 0; i < after.length; i++) {
                const hook = after[i];
                operation.assertCurrent();
                if (hook.async) data = await Hooks[hook.method](data, operation);
                else data = Hooks[hook.method](data, operation);
            }

            if (entity.name === 'document' && data.id !== item.id) {
                throw new Error('The server did not acknowledge the document that was sent.');
            }
            if (entity.name === 'document' && item.status !== 'D' && data.status === 'D') {
                throw new HttpError(409, DELETED_DOCUMENT_MESSAGE);
            }
            if (entity.name === 'document' && data.addedOn > item.addedOn) {
                // The server accepted this historical version, but its current
                // document is newer. Keep the original File/note on this device
                // for an explicit correction rather than reporting upload success.
                throw new HttpError(409, HISTORICAL_DOCUMENT_MESSAGE);
            }

            // OK to retire the acknowledged operation now — and ONLY it (use
            // item, NOT data, as the addedOn date may have changed!). The
            // removeByEntityId fallback that used to follow wiped every queued
            // operation for this id, silently discarding a newer unsent edit
            // enqueued during this call's in-flight window (OWA-786,
            // CXR-4-004/CU-5-E).
            if (lineage) {
                // OWA-998: retire it and rebase the saves that continue it in one queue
                // transaction; report the success only once that has committed
                if (!await acknowledgeTaskSave(operation, item as unknown as Task, lineage, data as unknown as Task)) return undefined;
            }
            else {
                const removeResult = await removeForSession(operation, entity.name, item.addedOn);
                if (removeResult.isErr()) console.warn(removeResult.unwrapErr());
            }
            if (entity.name === 'document') {
                operation.assertCurrent();
                const retained = await idb.supersedeOlderDocumentVersions(queuedItem);
                operation.assertCurrent();
                if (retained.isErr()) console.warn(retained.unwrapErr());
            }

            // Saved, so set the persisted flag to true
            data.persisted = true;

            // Tell redux that we succeeded
            dispatchCurrent(operation, entity.saveSuccess(data));

            // Return the data in case this was called synchronously
            return data as T;
        }
    }
    catch (err) {
        operation.assertCurrent();
        // This could be an error outside of HTTP
        console.error(`%c🌐 API: Error calling POST on ${entity.name}`, logFormatErr, err);

        // OWA-1149: a document's 413 is a size refusal the document hook (processDocument) has
        // already shown as a warning kept out of telemetry; its message can name a client's file.
        const sizeRefusal = entity.name === 'document' && err instanceof HttpError && err.status === 413;
        if (!sizeRefusal) Alert.add(new Alert(Alert.ERROR, item.id, item.addedOn, err.message));

        // Tell redux that we failed.  Use a standard 500 error message if one is not given
        await reportSaveFailure(operation, entity, entity.name === 'document' ? queuedItem : item,
            err.status || 500, err.clientMessage || err.message);

        // A document may be the only copy of a client's work. Do not replace
        // its local state with an older server version after an upload error.
        if (entity.name !== 'document') dispatchCurrent(operation, entity.fetchOneQueue(item.id));

        // Throw the error to the caller
        if (!(err instanceof HttpError && err.status < 500)) captureException(err, {extra: {}, tags: ['api']});

        throw err;
    }
}

/**
 * Restore (PATCH) an item
 *
 * @param {EntityMapper<<T>>} entity - The name of the entity (and queue)
 * @param {string} entity.name - name of the entity
 * @param {function} entity.restoreSuccess - redux action to call on success
 * @param {function} entity.restoreFailure - redux action to call on failure
 * @param {function} entity.fetchOneQueue - redux action to re-fetch this item
 * @param {<T>} item - The item to restore
 * @returns {Promise<<T>>}
 */
export async function restore<T extends EntityType>(entity: EntityMapper<T>, item: T, signal?: AbortSignal): Promise<HttpStatusResult | T | undefined> {
    const operation = captureRequestSession();
    const boundApiCall = makeSessionApiCaller(operation);
    // OWA-998: a task restore is checked like a save
    const lineage = entity.name === 'task' ? lineageOf(item) : null;
    try {
        if (logging) console.debug(`%c🌐 API: restore for ${entity.name}`, logFormat);

        // Get hooks for this type
        const {before, after} = Hooks.getHooks(entity.name, 'restore');

        // Do any 'before' hooks. Note that addedOn must not change!
        const addedOn = item.addedOn;
        for (let i = 0; i < before.length; i++) {
            const hook = before[i];
                operation.assertCurrent();
            if (hook.async) {
                item = await Hooks[hook.method](item, operation);
            }
            else {
                item = Hooks[hook.method](item, operation);
            }
        }
        item.addedOn = addedOn;

        const result = isDemo()
                       ? fakeRestore(item)
                       : await boundApiCall(`/${entity.name}`, 'PATCH', lineage ? taskWire(item) : item, signal);

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /${entity.name}`, logFormatErr);
            return {status: 503, body: `Error calling server, returned a null for URL /${entity.name}`};
        }

        const {status, body} = result;

        // OWA-998: a task restore refused as stale (the task changed after the deletion it was
        // shown) is held for the user like a refused save
        const staleConflict = lineage ? staleConflictOf(status, body) : null;
        if (staleConflict) {
            await refuseTaskSave(operation, action => dispatchCurrent(operation, action), item as unknown as Task, lineage!, staleConflict);
            return {status, body};
        }
        // OWA-786 (CXR-4-004): 5xx no longer terminal on the restore path
        // either — same durable-intent rule as save/remove.
        const isUnrecoverable =
            [400, 403, 404, 405, 406, 409, 411, 413, 414, 415, 416, 422].includes(status);

        // Handle HTTP error
        if (status >= 400) {
            console.error('%c🌐 API: Error restoring entity %o %o', logFormatErr, status, body);

            // If unrecoverable error, remove from queue and alert user
            if (isUnrecoverable) {
                const removeResult = await removeForSession(operation, entity.name, item.addedOn);
                if (removeResult.isErr()) console.warn(removeResult.unwrapErr());
            }

            // Tell redux that we failed. Handle a bare-string body like save() does:
            // 409s from the server used to arrive as JSON strings, and reading
            // body.clientMessage off a string yields undefined — the resulting
            // message-less fatalError was treated as clean by processQueue and
            // re-sent unbounded (OWA-685, the redFlag 409/404 storm).
            dispatchCurrent(operation, entity.restoreFailure(
                item,
                status,
                typeof body === 'string' ? body : (body.clientMessage || body.message)
            ));

            // If unrecoverable, fetch the old value from the database and refresh
            if (isUnrecoverable) dispatchCurrent(operation, entity.fetchOneQueue(item.id, item.addedOn));
        }
        else {
            // Handle normal processing
            if (logging) console.debug(`%c🌐 API: HTTP ${status}: ${entity.name}.save: ${body.clientMessage}`, logFormat);

            let data: EntityType = cast<T>(entity.name, body);

            // Saved, so set the persisted flag to true
            data.persisted = true;
            data.restore = false;

            // Do any 'after' hooks
            for (let i = 0; i < after.length; i++) {
                const hook = after[i];
                operation.assertCurrent();
                if (hook.async) data = await Hooks[hook.method](data, operation);
                else data = Hooks[hook.method](data, operation);
            }

            // OK to remove from queue now (use item, NOT data, as the addedOn date may have changed!)
            if (lineage) {
                // OWA-998: see save()
                if (!await acknowledgeTaskSave(operation, item as unknown as Task, lineage, data as unknown as Task)) return undefined;
            }
            else {
                const removeResult = await removeForSession(operation, entity.name, item.addedOn);
                if (removeResult.isErr()) console.warn(removeResult.unwrapErr());
            }

            // Tell redux that we succeeded
            dispatchCurrent(operation, entity.restoreSuccess(data));

            // Return the data in case this was called synchronously
            return data as T;
        }
    }
    catch (err) {
        operation.assertCurrent();
        // This could be an error outside of HTTP
        console.error(`%c🌐 API: Error calling PATCH on ${entity.name}`, logFormatErr, err);

        // Tell redux that we failed.  Use a standard 500 error message if one is not given
        dispatchCurrent(operation, entity.restoreFailure(item, err.status || 500, err.clientMessage || err.message));

        // Unrecoverable, fetch the old value from the database and refresh
        dispatchCurrent(operation, entity.fetchOneQueue(item.id));

        // Throw the error to the caller
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Secondary descriptor for a trashed row when there's no case/lead parent —
 * e.g. a task's categories + activity types.
 */
export type TrashContext = {
    parentType?: 'matter' | 'lead' | 'contact';
    parentLabel?: string | null;
    parentCode?: string | null;
    clients?: string[];
    extra?: string;
};

/**
 * A soft-deleted entity as returned by `GET /misc/trash`.
 */
export type TrashedEntity = {
    entityType: string;
    id: string;
    label: string;
    deletedBy: string;
    deletedOn: number;
    entity: any;
    context: TrashContext | null;
};

/**
 * Fetch the current firm's trash (soft-deleted entities). The server sources
 * firmId/userId from the JWT (assertSameFirm), so no firm/user arguments are
 * passed here. Throws on a non-2xx response so callers can surface the message;
 * resolves to an array (possibly empty) on success.
 *
 * @returns {Promise<TrashedEntity[]>}
 */
export async function getTrash(): Promise<TrashedEntity[]> {
    const result = await makeSecureApiCall('/misc/trash', 'GET', {});

    if (!result) {
        throw new Error('Failed to load trash');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || result.body?.clientMessage || `Failed to load trash (${result.status})`);
    }

    const items = result.body?.items;
    return Array.isArray(items) ? items : [];
}

/**
 * Save (POST) a group of (similar) items
 *
 * @param {EntityMapper<<T>>} entity - The name of the entity (and queue)
 * @param {<T>[]} items - The items to save, all the same type
 * @returns {Promise<<T>[]>}
 */
export async function saveMany<T extends EntityType>(entity: EntityMapper<T>, items: T[], signal?: AbortSignal): Promise<T[] | undefined> {
    const operation = captureRequestSession();
    const boundApiCall = makeSessionApiCaller(operation);
    if (!store.getState().misc.isConnected) return [];

    try {
        if (logging) console.debug(`%c🌐 API: save for ${entity.plural}`, logFormat);

        // Get hooks for this type
        const {before, after} = Hooks.getHooks(entity.name, 'save');

        // Do any 'before' hooks on each individual item. Note that addedOn must not change!
        const postHookItems: T[] = [];
        // OWA-998: each task save's lineage, read before any hook can replace the object
        const postHookLineage: ReturnType<typeof lineageOf>[] = [];

        for (let i = 0; i < items.length; i++) {
            let item = items[i];
            if (!item) console.warn(`%c🌐 API: saveMany called with a null item at index ${i} for ${entity.name}`, logFormat);

            else {
                const addedOn = item.addedOn;
                const lineage = lineageOf(item);

                // Call the before hook
                try {
                    for (let j = 0; j < before.length; j++) {
                        const hook = before[j];
                        operation.assertCurrent();

                        if (hook.async) item = await Hooks[hook.method](item, operation);
                        else item = Hooks[hook.method](item, operation);
                        operation.assertCurrent();
                    }
                }
                catch (err) {
                    operation.assertCurrent();
                    if (entity.name !== 'document') throw err;
                    // A document upload may already have succeeded for an
                    // earlier sibling. Keep that sibling in the batch and
                    // report this document's failure only to its own row.
                    await reportSaveFailure(operation, entity, items[i], err.status || 500, err.clientMessage || err.message);
                    continue;
                }

                // Restore the addedOn timestamp
                item.addedOn = addedOn;

                // Add this to the new _items array
                postHookItems.push(item);
                postHookLineage.push(lineage);
            }
        }

        if (postHookItems.length === 0) return [];

        // We now make one big call with all the items. Individual items many pass or fail.
        // Right now we just get back a single code telling us if everything passed, or if one or more items failed.
        // If everything passed, then the body will contain fresh versions of all items.
        // If not everything passed, then the body will contain all items, some fresh and some with fatalErrors.
        const result = isDemo()
                       ? fakeUpdates(postHookItems)
                       : await boundApiCall(`/${entity.plural}`, 'POST', entity.name === 'task' ? postHookItems.map(taskWire) : postHookItems, signal);
        const status = result?.status || 500;
        const body = result?.body || {};

        // Note that this is for the call as a whole.  This should only happen if something is seriously out of whack.
        // Even a definitive rejection must not retire a document batch: a
        // queued document can be the only copy of its contents.
        const isUnrecoverable =
            entity.name !== 'document' && [400, 403, 404, 405, 406, 409, 411, 413, 414, 415, 416, 422].includes(status);

        const statusText = (status: number, isUnrecoverable: boolean) => status >= 400
                                                                         ? isUnrecoverable
                                                                           ? 'unrecoverable'
                                                                           : 'recoverable'
                                                                         : 'saved';

        // console.debug(`API: Save for ${entity.plural}`, status, statusText(status, isUnrecoverable), body);

        // Handle top-level HTTP error
        if (status >= 400) {
            console.error('%c🌐 API: : Error saving entities %o %o', logFormatErr, status, body);

            // If unrecoverable error, remove from queue
            if (isUnrecoverable) {
                for (let i = 0; i < postHookItems.length; i++) {
                    const removeResult = await removeForSession(operation, entity.name, postHookItems[i].addedOn);
                    if (removeResult.isErr()) console.warn(removeResult.unwrapErr());
                }
            }

            const message = typeof body === 'string' ? body : (body.clientMessage || body.message);
            // DOCUMENT_SAVE_MANY_FAILURE leaves document state unchanged. Mark
            // each queued document instead, retaining its visible error.
            if (entity.name === 'document') {
                for (const item of postHookItems) await reportSaveFailure(operation, entity, item, status, message);
            }
            else dispatchCurrent(operation, entity.saveManyFailure(postHookItems, status, message));

            // If unrecoverable, fetch the old value from the database and
            // refresh. A failed SAVE must never be converted into a restore:
            // the restoreQueue dispatch that used to live here restamped every
            // row, changed its operation to restore=true, and re-enqueued the
            // batch as semantically different PATCH work (OWA-786, CXR-5-002).
            if (isUnrecoverable) {
                for (let i = 0; i < postHookItems.length; i++) {
                    dispatchCurrent(operation, entity.fetchOneQueue(postHookItems[i].id, postHookItems[i].addedOn));
                }
            }
        }

        // No top-level error
        else {
            // Handle normal processing
            if (logging) console.debug(`%c🌐 API: HTTP ${status}: ${entity.name}.saveMany`, logFormat);

            // In this case, the body is an array of JSON objects (of the same type).
            // Some may have fatalErrors, in which case we need to handle those errors
            const convertedResults: T[] = [];
            const succeeded: T[] = [];

            for (let i = 0; i < body.length; i++) {
                // The response is aligned with what was POSTed (postHookItems),
                // NOT with the caller's original array — `items` can contain
                // nulls that were filtered out above, so indexing it here
                // removed the WRONG queue row (OWA-786, CU-5-J).
                const sent = postHookItems[i];
                const item = cast<T>(entity.name, body[i]);

                // OWA-998: a task save refused as stale stays queued for the user (see save())
                const staleConflict = entity.name === 'task' ? staleConflictOf(Number(item.fatalError?.status), item.fatalError) : null;
                if (staleConflict && sent) {
                    await refuseTaskSave(operation, action => dispatchCurrent(operation, action), sent as unknown as Task, postHookLineage[i], staleConflict);
                    continue;
                }

                // Was there an error?
                if (item.fatalError?.status >= 400) {
                    const {message, status} = item.fatalError;

                    // Is this a recoverable error? A per-item 5xx is transient:
                    // the row stays queued for bounded retry (OWA-786, CXR-5-002).
                    const isUnrecoverable =
                        entity.name !== 'document' && [400, 403, 404, 405, 406, 409, 411, 413, 414, 415, 416, 422].includes(status);

                    console.debug(`%c🌐 API: Error saving ${entity.name} ${item.id} ${status} ${statusText(status, isUnrecoverable)} ${message}`,
                        logFormat
                    );

                    if (isUnrecoverable) {
                        // Remove the exact operation from the queue
                        console.warn('%c🌐 API: : Unrecoverable, removing from queue %d %d', logFormat, sent?.addedOn, item.addedOn);
                        if (sent?.addedOn !== item.addedOn) console.warn('%c🌐 API: addedOn <> %o %o', logFormat, sent, item);
                        const removeResult = await removeForSession(operation, entity.name, sent?.addedOn);
                        if (removeResult.isErr()) console.warn(removeResult.unwrapErr());
                    }

                    // In any case, we failed to save
                    await reportSaveFailure(operation, entity, entity.name === 'document' ? sent : item, status, message);

                    // A failed save is never converted into a restore (the
                    // restoreQueue dispatch that used to live here — OWA-786,
                    // CXR-5-002); on a terminal outcome refetch server truth.
                    if (isUnrecoverable) dispatchCurrent(operation, entity.fetchOneQueue(item.id, sent?.addedOn));

                    // Add to results
                    convertedResults.push(item);
                }

                // Save successful for this item
                else {
                    if (entity.name === 'document' && sent?.id !== item.id) {
                        await reportSaveFailure(operation, entity, sent, 502, 'The server did not acknowledge this document.');
                        continue;
                    }
                    // We need to cast the item from JSON to a real entity
                    let data = cast<T>(entity.name, item);

                    // Saved, so set the persisted flag to true
                    data.persisted = true;

                    // Do any 'after' hooks
                    for (let i = 0; i < after.length; i++) {
                        const hook = after[i];
                operation.assertCurrent();
                        if (hook.async) data = await Hooks[hook.method](data, operation);
                        else data = Hooks[hook.method](data, operation);
                    }

                    // OK to retire the acknowledged operation now (use the sent
                    // row, NOT data, as the addedOn date may have changed!)
                    if (entity.name === 'task' && sent) {
                        // OWA-998: see save(); a slot whose acknowledgement did not commit is
                        // not reported as saved, and is sent again
                        if (!await acknowledgeTaskSave(operation, sent as unknown as Task, postHookLineage[i], data as unknown as Task)) continue;
                    }
                    else {
                        const removeResult = await removeForSession(operation, entity.name, sent?.addedOn);
                        if (removeResult.isErr()) console.warn(removeResult.unwrapErr());
                    }
                    if (entity.name === 'document') {
                        operation.assertCurrent();
                        const queuedDocument = items.find(item => item?.addedOn === sent.addedOn);
                        const retained = await idb.supersedeOlderDocumentVersions(queuedDocument || sent);
                        operation.assertCurrent();
                        if (retained.isErr()) console.warn(retained.unwrapErr());
                    }

                    convertedResults.push(data);
                    succeeded.push(data);
                }
            }

            // Tell redux that we succeeded — with ONLY the items that actually
            // did. Dispatching failed items through the success action taught
            // every reducer to persist rows the server rejected (OWA-786).
            dispatchCurrent(operation, entity.saveManySuccess(succeeded));

            // Return the data in case this was called synchronously
            return convertedResults;
        }
    }
    catch (err) {
        operation.assertCurrent();
        // This could be an error outside of HTTP
        console.error(`%c🌐 API: Error calling POST on ${entity.plural}`, logFormatErr, err);

        // A document before-hook can fail before any entity POST. Keep every
        // original queued payload and report the error for each document.
        if (entity.name === 'document') {
            for (const item of items) {
                if (item) await reportSaveFailure(operation, entity, item, err.status || 500, err.clientMessage || err.message);
            }
        }
        else dispatchCurrent(operation, entity.saveManyFailure(items, err.status || 500, err.clientMessage || err.message));

        // Unrecoverable, fetch the old value from the database and refresh.
        // Skip null entries — the caller's array may contain them (OWA-786).
        for (let i = 0; i < items.length; i++) {
            if (!items[i]) continue;
            if (entity.name !== 'document') dispatchCurrent(operation, entity.fetchOneQueue(items[i].id, items[i].addedOn));
        }

        // Throw the error to the caller
        if (!(err instanceof HttpError && err.status < 500)) captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Save (POST) an item directly (with or without redux)
 *
 * Returns an object if successful, otherwise throws an error.
 *
 * Actions are optional, include if you want to automatically save to Redux
 *
 * @param {string} entityName
 * @param {<T>}item
 * @param {function} successAction - redux action to call on success
 * @param {function} failureAction - redux action to call on failure
 * @returns {Promise<{status: number, item: <T>, errorMessage: string}>} - Object is {status, item, errorMessage}
 */
export async function saveDirect<T extends EntityType>(entityName: string, item: T, successAction: Function, failureAction: Function): Promise<Result<T, Error>> {
    try {
        const operation = captureRequestSession();
        if (logging) console.debug(`%c🌐 API: saveDirect for ${entityName}`, logFormat);

        const result = await makeSessionApiCaller(operation)(`/${entityName}`, 'POST', item);
        operation.assertCurrent();

        if (!result) return Err(new Error(`Error calling server, returned a null for URL /${entityName}`));

        const {status, body} = result;

        if (successAction && failureAction) {
            if (status >= 400) {
                // Handle HTTP errors 422 (bad message) and 500
                if (logging) console.debug('%c🌐 API: Error saving entity %o', logFormat, status);

                dispatchCurrent(operation, failureAction(item, status, body.clientMessage || body.message));
            }
            else {
                if (logging) console.debug(`%c🌐 API: HTTP ${status}: ${entityName}.save: ${body.clientMessage}`, logFormat);

                const data = cast<T>(entityName, body);

                // Saved, so set the persisted flag to true
                data.persisted = true;
                item.persisted = true;

                dispatchCurrent(operation, successAction(data));
            }
        }

        if (status < 400) return Ok(cast<T>(entityName, body) as unknown as T);
        else return Err(new Error(body.clientMessage || body.message));
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling POST on ${entityName}`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return Err(err);
    }
}

/**
 * Helper function to process multiple result sets.
 *
 * Do load hooks here
 *
 * @param {EntityMapper<<T>>} entity - The entity, including name and fetchSuccess/Failure actions
 * @param {<T>[]} body - The result body from the call
 * @param {boolean} partial - is this a partial result set?
 * @param {number} lastFetch - last fetch time (max dbAddedOn)
 * @returns {Promise<>}
 */
async function processResults<T extends EntityType>(entity: EntityMapper<T>, body: T[], partial: boolean, lastFetch: number | undefined, operation: RequestSession): Promise<void> {
    operation.assertCurrent();
    // Response will be null or an array
    const results: EntityType[] = [];

    if (logging) console.debug(`%c🌐 API: processResults for ${body.length} ${entity.name} entries`, logFormat);

    if (body) {
        for (let j = 0; j < body.length; j++) {
            if (j % 100 === 0 && j > 0) await new Promise(r => setTimeout(r, 0));
            operation.assertCurrent();

            const res = body[j];

            if (res.fresh === true) console.error('%c🌐 API: : Results from a fetch should not be FRESH!', logFormatErr);

            let item = cast<T>(entity.name, res);
            item.persisted = true;
            item.fresh = false;

            // Get hooks (only applies for 'after' for a find)
            const {after} = Hooks.getHooks(entity.name, 'find');

            // Call any 'after' hooks
            for (let i = 0; i < after.length; i++) {
                const hook = after[i];
                operation.assertCurrent();
                if (hook.async) item = await Hooks[hook.method](item, operation);
                else item = Hooks[hook.method](item, operation);
                operation.assertCurrent();
            }

            results.push(item);
        }
    }

    // Figure out what to send back - use queued items in favor of returned items
    const queuedResult = await idb.getSingleStore(entity.name);
    operation.assertCurrent();
    let merged: EntityType[] = results;
    if (queuedResult.isErr()) {
        // IDB unavailable: preserve the existing fallback to the server result.
        if (logging) console.warn(`%c🌐 API: processResults skipping IDB queue merge for ${entity.name} — ${queuedResult.unwrapErr().message}`, logFormatErr);
    } else {
        const queued = entity.name === 'document'
            ? queuedResult.unwrap().filter(item => !item.supersededByAddedOn)
            : queuedResult.unwrap();
        if (queued.length > 0) {
            // Merge server results with queued items, preferring the latest queued version
            merged = [];
            const idKey = `${entity.name}Id`;
            const queuedMap = new Map<string, any[]>();

            queued.forEach(item => {
                const id = item[idKey];
                if (!queuedMap.has(id)) {
                    queuedMap.set(id, []);
                }
                queuedMap.get(id)!.push(item);
            });

            for (let k = 0; k < results.length; k++) {
                if (k % 100 === 0 && k > 0) await new Promise(r => setTimeout(r, 0));
                operation.assertCurrent();

                const item = results[k];

                // Is there a match in the queue? (O(1) lookup instead of O(m) filter)
                const matches = queuedMap.get(item[idKey]) || [];
                if (matches.length > 0 && logging) {
                    console.debug(`%c🌐 API: processResults found ${matches.length} entries in the ${entity.name} queue`, logFormat);
                }
                const latest = (matches.length > 0) ? matches.reduce(latestItem) : null; // Get the latest one, or null

                // An old failed document must not hide a newer server version.
                merged.push(latest && (entity.name !== 'document' || latest.addedOn > item.addedOn) ? latest : item);
            }

            if (entity.name === 'document') {
                // Rejected creates have no server row to replace, but their notes and
                // files still need to remain visible after a full refresh.
                const returnedIds = new Set(results.map(item => item[idKey]));
                for (const [id, matches] of queuedMap) {
                    if (!returnedIds.has(id)) merged.push(matches.reduce(latestItem));
                }
            }

        }
    }
    operation.assertCurrent();
    store.dispatch(entity.fetchSuccess(merged, partial, lastFetch));
    tabSession.recordFetchedEntity(operation, entity.name);
}

/**
 * Return all objects meeting the criteria passed (or all objects).
 *
 * Note that firms is a special case, so we return something different there.
 *
 * Otherwise, always filtered on firmId.
 *
 * @param {string} entityName
 * @param {uuid} entityId
 * @returns {Promise<void>}
 */
export async function findOne<T extends EntityType>(entityName: string, entityId: uuid): Promise<T | null> {
    const operation = captureRequestSession();
    if (logging) console.debug(`%c🌐 API: findOne ${entityName}`, logFormat);

    // Make the call to the server
    // Looking for a single result set
    const result = await makeSessionApiCaller(operation)(`/${entityName}/${entityId}`, 'GET', {});
    operation.assertCurrent();

    const {status, body} = result;

    // 404 errors are ok, we just return null
    if (status === 404) return null;

    // If a server error
    else if (status >= 400) {
        // Handle HTTP error
        throw new Error(`🌐 API: ${entityName}.findOne HTTP ${status}: ${body.clientMessage}`);
    }
    else {
        // Status is ok, parse results and return
        const item: EntityType = cast<T>(entityName, body);
        item.persisted = true;
        item.fresh = false;

        return item as T;
    }
}

/**
 * Return all objects meeting the criteria passed (or all objects).
 *
 * @param {'full'|'partial'} refreshType - full refresh returns a full copy of the database, partial will only return
 *                                         new data
 * @param {EntityMapper<<T>>[]} entities - array of objects of type (name, successAction, failureAction) one or more entity names
 * @param {object} [options] - options to pass through (see search.js)
 * @param {object} [sort] - sort criteria to pass through (see Dao classes)
 * @returns {Promise<number>}
 */
/**
 * The entity types whose deleted rows the server should return for this fetch.
 * - The schema-flagged types keep their deleted rows (history, restore): RETAINED_DELETED_TYPES (OWA-68).
 * - Partial fetches also need deleted tasks, so the reducer can remove them from state. Full fetches never
 *   return deleted tasks (they simply won't be in the result set).
 *
 * Returns a fresh array each call, so appending never changes the shared list.
 *
 * @param {'full' | 'partial'} refreshType
 * @returns {string[]}
 */
export function deletedEntryTypesFor(refreshType: 'full' | 'partial'): string[] {
    const types = [...RETAINED_DELETED_TYPES];
    if (refreshType === 'partial') types.push('task');
    return types;
}

export async function findMany<T extends EntityType>(refreshType: 'full' | 'partial', entities: EntityMapper<T>[], options: object | null = null, sort: object | null = null, signal?: AbortSignal, operation = captureRequestSession()): Promise<number | undefined> {
    operation.assertCurrent();
    const boundApiCall = makeSessionApiCaller(operation);
    if (logging) console.debug('%c🌐 API: findMany %o %o %o', logFormat, refreshType, options, sort);

    // If no connection to the server, just return
    if (!store.getState().misc.isConnected) return;
    if (!Auth.isAuthenticated()) {
        console.error('Can not call findMany if not authenticated!');
        return;
    }

    let lastPartials: LastPartial[] = [];

    entities.forEach(e => {
        if (refreshType === 'full') lastPartials.push({name: e.name, maxDbAddedOn: 0});
        else {
            const maxDbAddedOn = store.getState()[e.plural].maxDbAddedOn || 0;
            lastPartials.push({name: e.name, maxDbAddedOn});
        }
    });

    if (logging) console.debug(`%c🌐 API: refreshType ${refreshType} %o`, logFormat, lastPartials);

    const deletedEntryTypes = deletedEntryTypesFor(refreshType);

    try {
        const t0 = performance.now();
        const result = await boundApiCall('/general/batch', 'POST', {
            entities: entities.map(e => e.name),
            options,
            sort,
            lastPartials,
            refreshType,
            getDeletedForEntities: deletedEntryTypes,
            tableNames: entities.map(e => e.table)
        }, signal);
        const batchMs = (performance.now() - t0).toFixed(0);

        /**
         * Status will be an HTTP code, body contains both the results and the new lastPartial value.
         */
        const status = result?.status;
        const body = result?.body;
        const linkCount = body?.links?.length ?? 0;
        if (logging) console.debug(`⏱ /general/batch round-trip: ${batchMs}ms (${refreshType}, ${entities.length} entities, ${linkCount} links)`);
        if (logging) console.debug(`%c🌐 API: findMany call returned ${status} %o`, logFormat, body);

        // If no status, this could mean that the server died or restarted during the request
        if (!status) {
            console.warn('🌐 API: No status, server likely disconnected or reloaded');
            dispatchCurrent(operation, StatusBarActions.fetchError('Server Disconnected'));
        }

        // JWT Errors
        else if (status === 403 || (body && body.message === "jwt expired")) {
            // This shouldn't be happening but ignoring the error works fine
            console.warn(`🌐 API: findMany failed with status code ${status} and body`, body);
            Alert.addSimpleError('Session logged out, please log out and log back in');
        }

        // If a server error, let the user know in the status bar
        else if (status >= 400) {
            // Handle HTTP error
            console.warn('🌐 API: clientMessage', body);
            dispatchCurrent(operation, StatusBarActions.fetchError(body.clientMessage ?? body));
        }

        // Status is ok, parse results and pass to redux
        else {
            // While we are fetching, let the user know that there will be some interruptions by showing the wait cursor
            document.body.style.cursor = 'progress';

            /**
             * The body element holds three pieces:
             * - newPartials stores a map of entity names to dbAddedOns (the new lastPartials)
             * - processed is the set of processed results, keyed into an object (e.g. object.task => Task[])
             * - links is a map of entity type names to urls
             */
            let newPartials: any = body.newPartials;
            let processed: any = body.processed;
            let links: any = body.links;

            // Rehydrate the map of newPartials
            newPartials = new Map(newPartials);

            /**
             * Rehydrate the map of links (entity, link to results)
             */
            const linkMap: Map<string, string> = new Map(links);

            let total = 0;
            Object.keys(processed).forEach(key => total += processed[key].length);

            /**
             * Get the total number of returned rows over all result sets to keep track of fetch progress.
             * After each set is processed, we will update the status bar.
             */
            const totalCount: number = Object.values(processed).reduce((t: number, c: any[]) => t + c.length, 0) as number;

            /**
             * Temporary variable to keep track of how many rows have been processed.
             */
            let count = 0;

            const promises: Promise<any>[] = [];

            async function processEntity<T extends EntityType>(data: any, entity: EntityMapper<T>) {
                // A successful batch omits empty and unchanged entities. Keep existing rows,
                // but count the completed fetch unless this entity still has a file to load.
                if (!data && !linkMap.has(entity.name)) tabSession.recordFetchedEntity(operation, entity.name);
                if (data) {
                    // Update the count with the number of rows we are about to process.
                    count += data.length;

                    // Process the results into redux store
                    try {
                        await processResults(
                            entity,
                            data,
                            refreshType === 'partial',
                            newPartials.get(entity.name), operation
                        );

                        // Update status bar with partial fetch results
                        dispatchCurrent(operation, StatusBarActions.fetchSuccess(count, totalCount));
                    }
                    catch (err) {
            operation.assertCurrent();
                        console.error(`%c🌐 API: Error processing ${entity.name} results (${data.length} rows): ${err.message}`, logFormatErr);
                    }
                }
            }

            linkMap.forEach((value, key) => {
                const url = `${URI}/${value}`;

                const fetchResult = fetchFileWithProgress(url, true, undefined, operation.signal)
                    .then(data => {
                        const entity = entities.find(e => e.name === key);
                        if (!entity) return;
                        return processEntity<T>(data, entity);
                    })
                    .catch(err => console.error(`%c🌐 API: Error fetching light entity file ${value} (key=${key}): ${err.message}`, logFormatErr, err))
                    .finally(() => {
                        // Delete the file on the server
                        boundApiCall(`/general/file`, 'DELETE', {file: value})
                            .catch(err => console.warn(`🌐 API: Could not delete temp file ${value}: ${err.message}`));
                    })

                promises.push(fetchResult);
            });

            // Process each entity, one at a time.  Send results to redux reducers.
            for (let i = 0; i < entities.length; i++) {
                /**
                 * The name of the entity.
                 */
                const entity = entities[i];

                const data = processed[entity.name];

                promises.push(processEntity(data, entity));
            }

            await Promise.allSettled(promises);
                operation.assertCurrent();

            // Let redux know that we had a successful fetch (and are done fetching)
            dispatchCurrent(operation, StatusBarActions.fetchSuccess(0, 0));

            return newPartials;
        }
    }
    catch (err) {
            operation.assertCurrent();
        // Something very strange must have happened for fetch to return something like this.
        console.error(`%c🌐 API: ${err.message}`, logFormatErr);
        captureException(err, {extra: {}, tags: ['api']});
        dispatchCurrent(operation, StatusBarActions.fetchError(err.message));
    }
    finally {
        // Return the cursor to default, if it hasn't already been changed
        document.body.style.cursor = 'default';
    }
}

/**
 * Tiered version of findMany that fetches entities in dependency order.
 * Uses one /general/batch call per tier for efficient server processing,
 * while ensuring referenced entities are in the store before dependents arrive.
 *
 * Heavy entities (task, document, comment) have their HTTP request fired at t=0
 * so the server starts working immediately. The response is processed after
 * all light tiers complete, so progress tracking stays sequential and accurate.
 *
 * Fetch timeline:
 *   t=0  Fire heavy HTTP request (task, document, comment) — don't await
 *   t=0  Tier 1: Leaf/config entities (findMany, await, shows progress)
 *   t=1  Tier 2: Core identity — firm, contact, user, group (findMany, await)
 *   t=2  Tier 3: Remaining business entities (findMany, await)
 *   t=3  Process heavy response (should already be available, shows progress)
 *
 * Tiers derived from docs/architecture/dependency-graph.md.
 *
 * Portal users should use the standard findMany() instead, as the server has
 * cross-entity data dependencies that require sequential batch processing.
 */
export async function findManyParallel<T extends EntityType>(
    refreshType: 'full' | 'partial',
    entities: EntityMapper<T>[],
    options: object | null = null,
    sort: object | null = null,
    signal?: AbortSignal
): Promise<void> {
    const operation = captureRequestSession();
    const boundApiCall = makeSessionApiCaller(operation);
    if (logging) console.debug('%c🌐 API: findManyParallel %o %o %o', logFormat, refreshType, options, sort);

    if (!store.getState().misc.isConnected) return;
    if (!Auth.isAuthenticated()) {
        console.error('Can not call findManyParallel if not authenticated!');
        return;
    }

    const deletedEntryTypes = deletedEntryTypesFor(refreshType);

    /**
     * Heavy entities: these tables are orders of magnitude larger than others (100-1000x).
     * Fire their HTTP request at t=0 so the server starts processing while we load
     * the lighter tiers. The response is processed after all light tiers complete.
     */
    const heavyNames = new Set(['task', 'document', 'comment']);
    const heavyEntities = entities.filter(e => heavyNames.has(e.name));
    const lightEntities = entities.filter(e => !heavyNames.has(e.name));

    // Build lastPartials for heavy entities and fire the HTTP request immediately
    let heavyPromise: Promise<any> | null = null;
    if (heavyEntities.length > 0) {
        const heavyPartials: LastPartial[] = heavyEntities.map(e => ({
            name: e.name,
            maxDbAddedOn: refreshType === 'full' ? 0 : (store.getState()[e.plural].maxDbAddedOn || 0)
        }));

        heavyPromise = boundApiCall('/general/batch', 'POST', {
            entities: heavyEntities.map(e => e.name),
            options,
            sort,
            lastPartials: heavyPartials,
            refreshType,
            getDeletedForEntities: deletedEntryTypes,
            tableNames: heavyEntities.map(e => e.table)
        }, signal);

        if (logging) console.debug(`%c🌐 API: findManyParallel heavy HTTP request fired (${heavyEntities.map(e => e.name).join(', ')})`, logFormat);
        // The light tiers can fail before this request is awaited. Observe its
        // rejection now; the original promise still throws when awaited below.
        void heavyPromise.catch(() => undefined);
    }

    /**
     * Dependency-aware fetch tiers for light entities (see docs/architecture/dependency-graph.md).
     * Tier 1: Leaf entities (no outgoing references) plus near-leaves that only reference other leaves.
     * Tier 2: Core identity entities — most referenced by everything else (contact 15+, user 12+ incoming refs).
     * Tier 3: Everything not in tier 1 or 2 (remaining business entities).
     */
    const tier1Names = new Set([
        'activityCategory', 'address', 'bankAccount', 'caseType', 'category',
        'docket', 'importExport', 'importSource', 'lawpay', 'lookup',
        'option', 'privilege', 'resource',
        'activityType', 'redFlagType', 'role' // depend only on tier-1 leaves
    ]);

    const tier2Names = new Set([
        'firm', 'contact', 'user', 'group', 'campaign'
    ]);

    const tiers = [
        {entities: lightEntities.filter(e => tier1Names.has(e.name)), label: 'Loading configuration'},
        {entities: lightEntities.filter(e => tier2Names.has(e.name)), label: 'Loading firm data'},
        {entities: lightEntities.filter(e => !tier1Names.has(e.name) && !tier2Names.has(e.name)), label: 'Loading practice data'},
    ];

    // Process light tiers sequentially — each findMany shows its own accurate progress
    for (const tier of tiers) {
        if (tier.entities.length === 0) continue;
        dispatchCurrent(operation, MiscActions.setLoadingMessage(tier.label));
        if (logging) console.debug(`%c🌐 API: findManyParallel tier: ${tier.label} (${tier.entities.length} entities)`, logFormat);

        await findMany(refreshType, tier.entities, options, sort, signal, operation);
    }

    // Process the heavy batch response — should be ready or nearly ready by now
    if (heavyPromise) {
        dispatchCurrent(operation, MiscActions.setLoadingMessage('Loading tasks and documents'));
        if (logging) console.debug('%c🌐 API: findManyParallel processing heavy batch', logFormat);

        try {
            document.body.style.cursor = 'progress';
            const result = await heavyPromise;
            operation.assertCurrent();
            const status = result?.status;
            const body = result?.body;

            if (!status) {
                console.warn('🌐 API: No status for heavy batch, server likely disconnected');
                dispatchCurrent(operation, StatusBarActions.fetchError('Server Disconnected'));
            } else if (status === 403 || (body && body.message === "jwt expired")) {
                console.warn(`🌐 API: findManyParallel heavy batch failed with ${status}`, body);
                Alert.addSimpleError('Session logged out, please log out and log back in');
            } else if (status >= 400) {
                console.warn('🌐 API: findManyParallel heavy batch error', body);
                dispatchCurrent(operation, StatusBarActions.fetchError(body.clientMessage ?? body));
            } else {
                const newPartials: Map<string, number> = new Map(body.newPartials);
                const processed: any = body.processed;
                const linkMap: Map<string, string> = new Map(body.links);

                const totalCount: number = Object.values(processed).reduce((t: number, c: any[]) => t + c.length, 0) as number;
                let count = 0;

                const promises: Promise<any>[] = [];

                // Handle file links (large entities returned as temp files)
                linkMap.forEach((value, key) => {
                    const url = `${URI}/${value}`;

                    const fetchResult = fetchFileWithProgress(url, true, undefined, operation.signal)
                        .then(async data => {
                            const entity = heavyEntities.find(e => e.name === key);
                            if (!entity) return;
                            count += data.length;
                            await processResults(entity, data, refreshType === 'partial', newPartials.get(entity.name), operation);
                            dispatchCurrent(operation, StatusBarActions.fetchSuccess(count, totalCount));
                        })
                        .catch(err => console.error(`%c🌐 API: Error fetching heavy entity file ${value} (key=${key}): ${err.message}`, logFormatErr, err))
                        .finally(() => {
                            boundApiCall('/general/file', 'DELETE', {file: value})
                                .catch(err => console.warn(`🌐 API: Could not delete ${value}: ${err.message}`));
                        });

                    promises.push(fetchResult);
                });

                // Handle inline data
                for (const entity of heavyEntities) {
                    const data = processed[entity.name];
                    if (!data && !linkMap.has(entity.name)) tabSession.recordFetchedEntity(operation, entity.name);
                    if (data) {
                        count += data.length;
                        const p = processResults(entity, data, refreshType === 'partial', newPartials.get(entity.name), operation)
                            .then(() => dispatchCurrent(operation, StatusBarActions.fetchSuccess(count, totalCount)))
                            .catch(err => console.error(`🌐 API: Error processing ${entity.name}: ${err.message}`, logFormatErr));
                        promises.push(p);
                    }
                }

                await Promise.allSettled(promises);
                operation.assertCurrent();
                dispatchCurrent(operation, StatusBarActions.fetchSuccess(0, 0));
            }
        } catch (err) {
            operation.assertCurrent();
            console.error(`🌐 API: findManyParallel heavy batch error: ${err.message}`, logFormatErr);
            captureException(err, {extra: {}, tags: ['api']});
            dispatchCurrent(operation, StatusBarActions.fetchError(err.message));
        } finally {
            document.body.style.cursor = 'default';
        }
    }
}

/**
 * Sign up a user to Auth0
 *
 * Auth results:
 {
 "email": "john@outlawpractice2.com",
 "user_metadata": {
 "firmId": "6dccb7c0-0434-4e4b-91e8-4dbb7a47aeb2",
 "userId": "f50c685e-fc27-432b-9324-5786b9a4c47c"
 },
 "email_verified": false,
 "user_id": "auth0|5a94cf76fbf06a78e8035ab4",
 "picture": "https://s.gravatar.com/avatar/c5af3e893bf6ab8218d33cf6bdccd5e8...",
 "identities": [{
 "connection": "Username-Password-Authentication",
 "user_id": "5a94cf76fbf06a78e8035ab4",
 "provider": "auth0",
 "isSocial": false
 }],
 "updated_at": "2018-02-27T03:24:38.077Z",
 "created_at": "2018-02-27T03:24:38.077Z"
 }
 *
 * @param {Firm} firm
 * @param {User} user
 * @param {Contact} contact
 * @param {Address} address
 * @param {boolean} [isInitialUser] - is this the first user in the firm?
 * @param {Contact} [firmContact] - if first user, we attach a new firm contact
 * @returns {Promise<SignupResult>}
 */
export async function signup(firm: Firm, user: User, contact: Contact, address: Address, isInitialUser?: boolean, firmContact?: Contact): Promise<Result<SignupResult, Error>> {
    if (logging) console.debug('%c🌐 API: signup %o %o %o %o', logFormat, firm, user, contact, isInitialUser);

    const path = isInitialUser ? '/auth/signup' : '/v2/auth/signupUser';
    const payload = isInitialUser
        ? {firm, user, contact, address, isInitialUser: true, firmContact}
        : {user, contact, isInitialUser: false};
    const result = await (isInitialUser ? makeApiCall : makeSecureApiCall)(path, 'POST', payload);

    if (!result) return Err(new Error(`Error calling server, returned a null for URL /auth/signup`));

    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error

        // Error 409 - Conflict means there is an existing firm, user or contact with the same ID,
        // which should only happen in testing

        // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request,
        // but there could be errors like "403 Unauthorized" if the server can not authenticate with the Auth0
        // management api
        if (logging) console.debug('%c🌐 API: signup returnError %d %s', logFormat, status, body.message || body.clientMessage);

        if (status === 401 || status === 403) { // Unauthorized
            return Err(new Error(body.message || body.clientMessage));
        }
        else if (status === 409) { // Duplicate
            return Err(new Error('This email specified is already in use.'));
        }
        else return Err(new Error(`Error ${status}, ${body.message || body.clientMessage}`));
    }
    else {
        if (logging) console.debug('%c🌐 API: signup returnOK %o', logFormat, body);

        // OWA-379: Server used to return the freshly-created default rows
        // (categories, lookups, etc.) under a `results` field for us to
        // dispatch into the store; that field name never matched what the
        // server actually sent (`entities`), so this branch always hit the
        // error fallback. Removed entirely — the post-Auth0 redirect runs
        // `polling.start()` → `fetchFromServer` which pulls everything,
        // making this pre-population redundant even when it worked.

        return Ok(body);
    }
}

/**
 * Get the uri for the customer portal.
 *
 * @param {uuid} customerId
 * @param {string} returnUrl
 * @returns {Promise<Result<string, HttpError>>} - string is the url of the portal
 */
export async function getPortalUri(customerId: uuid, returnUrl: string): Promise<Result<string, HttpError>> {
    try {
        const result = await makeSecureApiCall('/auth/stripe/portal', 'GET', {
            customerId,
            returnUrl
        });

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/stripe/portal`, logFormatErr);
            return Err(new HttpError(503, 'Error calling server, returned a null for URL /auth/stripe/portal'));
        }

        const status = result?.status;

        if (status >= 400) {
            const body: any = result.body;
            // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request
            console.error(`🌐 API: Error ${status}`, httpErrorMessage(status, body));
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        else return Ok(result.body.url);
    }
    catch (err) {
        captureException(err, {extra: {}, tags: ['api']});
        console.error(`🌐 API: ${err.message}`)
        return Err(err);
    }
}

/**
 * OWA-494: Is `email` available (not already used by an active user) in the
 * current firm? Backs the inline onBlur validation on the user create/edit
 * form. firmId is derived server-side from the JWT. Pass `excludeUserId` when
 * editing so the user's own current email doesn't flag itself.
 *
 * Returns true if available, false if taken. On a server/network error it
 * returns true (fail-open) — the submit path + the DB unique index remain the
 * real enforcement, so a flaky advisory check never blocks a legitimate save.
 */
export async function emailAvailable(email: string, excludeUserId?: uuid): Promise<boolean> {
    try {
        const params: Record<string, string> = {email};
        if (excludeUserId) params.excludeUserId = excludeUserId;

        const result = await makeSecureApiCall('/auth/emailAvailable', 'GET', params);
        if (!result || result.status >= 400) {
            console.warn(`🌐 API: emailAvailable check failed (status ${result?.status ?? 'null'}); treating as available`);
            return true; // fail-open
        }
        return result.body?.available !== false;
    }
    catch (err: any) {
        console.warn(`🌐 API: emailAvailable error: ${err?.message}; treating as available`);
        return true; // fail-open
    }
}

export async function authorize(webauthOptions: any) {
    const result = await makeExternalApiCall(IDP_CONFIG.domain,
        'GET',
        {}, // headers
        {
            response_type: 'token',
            clientId: IDP_CONFIG.clientId,
            connection: 'Username-Password-Authentication',
            redirect_uri: webauthOptions.callbackUrl
        }, // query params
        {} // body
    );

    if (!result) {
        console.error(`%c🌐 API: Error calling server, returned a null for URL ${IDP_CONFIG.domain}`, logFormatErr);
        return {status: 503, body: `Error calling server, returned a null for URL ${IDP_CONFIG.domain}`};
    }

    const {status, body} = result;

    if (status >= 400) throw new Error(body);
}

/**
 * Called after Auth0 does its login - we can check against the database.
 *
 * @param {uuid} userId
 * @param {any} userMetadata
 * @param {string} email
 * @returns {Promise<Result<VerifyLoginResult, HttpError>>}
 */
export async function verifyLogin(candidate?: LoginCandidate): Promise<Result<VerifyLoginResult, HttpError>> {
    if (!candidate && !store.getState().misc.isConnected) return Err(new HttpError(503, 'Not connected to the server'));

    try {
        const response = await makeSecureApiCall('/auth/verifyLogin', 'POST', {}, undefined, candidate);

        const status = response?.status || 503;
        const body = response?.body;

        if (logging) console.debug(`%c🌐 API: Login status ${status}`, logFormat);

        if (status === 401) {
            return Err(new HttpError(401, 'Incorrect user credentials supplied'));
        }
        else if (status === 404) {
            return Err(new HttpError(404, 'Could not find user while verifying login'));
        }
        else if (status === 422) {
            return Err(new HttpError(422, httpErrorMessage(422, body)));
        }
        else if (status !== 503 && status >= 400) {
            console.error(`%c🌐 API: Uncaught error in verifyLogin`, logFormatErr, status, body);
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        else return Ok({status, body: body || 'Server is unavailable'});
    }
    catch (err) {
        console.error(`%c🌐 API: Uncaught error in verifyLogin`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Called after Auth0 does its login - we can check against the database.
 *
 * @returns {Promise<Stripe.Subscription, HttpError>} - Returns an object with a Firm, User and Contact
 */
export async function getStripeSubscription(): Promise<Result<Stripe.Subscription, HttpError>> {
    try {
        const result = await makeSecureApiCall('/auth/subscription', 'GET', {});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/subscription`, logFormatErr);
            return Err(new HttpError(503, `Error calling server, returned a null for URL /auth/subscription`));
        }

        const {status, body} = result;

        if (status >= 400) {
            console.error(`%c🌐 API: Error in getStripeSubscription`, logFormatErr, status, body);
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        else return Ok(body);
    }
    catch (err) {
        console.error(`%c🌐 API: Error in getStripeSubscription`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return Err(err)
    }
}

export async function getStripeInvoices(): Promise<Result<StripeInvoiceSummary[], HttpError>> {
    try {
        const result = await makeSecureApiCall('/auth/invoices', 'GET', {});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/invoices`, logFormatErr);
            return Err(new HttpError(503, `Error calling server, returned a null for URL /auth/invoices`));
        }

        const {status, body} = result;

        if (status >= 400) {
            console.error(`%c🌐 API: Error in getStripeInvoices`, logFormatErr, status, body);
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        return Ok(body);
    }
    catch (err) {
        console.error(`%c🌐 API: Error in getStripeInvoices`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return Err(err);
    }
}

export async function getUpcomingInvoice(): Promise<Result<UpcomingInvoicePreview | null, HttpError>> {
    try {
        const result = await makeSecureApiCall('/auth/invoice/upcoming', 'GET', {});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/invoice/upcoming`, logFormatErr);
            return Err(new HttpError(503, `Error calling server, returned a null for URL /auth/invoice/upcoming`));
        }

        const {status, body} = result;

        if (status >= 400) {
            console.error(`%c🌐 API: Error in getUpcomingInvoice`, logFormatErr, status, body);
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        return Ok(body);
    }
    catch (err) {
        console.error(`%c🌐 API: Error in getUpcomingInvoice`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return Err(err);
    }
}

export async function cancelSubscription(): Promise<Result<Stripe.Subscription, HttpError>> {
    try {
        const result = await makeSecureApiCall('/auth/subscription/cancel', 'POST', {});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/subscription/cancel`, logFormatErr);
            return Err(new HttpError(503, `Error calling server, returned a null for URL /auth/subscription/cancel`));
        }

        const {status, body} = result;

        if (status >= 400) {
            console.error(`%c🌐 API: Error in cancelSubscription`, logFormatErr, status, body);
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        return Ok(body);
    }
    catch (err) {
        console.error(`%c🌐 API: Error in cancelSubscription`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return Err(err);
    }
}

export async function reactivateSubscription(): Promise<Result<Stripe.Subscription, HttpError>> {
    try {
        const result = await makeSecureApiCall('/auth/subscription/reactivate', 'POST', {});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/subscription/reactivate`, logFormatErr);
            return Err(new HttpError(503, `Error calling server, returned a null for URL /auth/subscription/reactivate`));
        }

        const {status, body} = result;

        if (status >= 400) {
            console.error(`%c🌐 API: Error in reactivateSubscription`, logFormatErr, status, body);
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        return Ok(body);
    }
    catch (err) {
        console.error(`%c🌐 API: Error in reactivateSubscription`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return Err(err);
    }
}

/**
 * When a user changes the encryption on a question, we need to retroactively change the encryption for that
 * field in the history (otherwise it will be left unencrypted in the history!).
 *
 * @param {Question} question - the question we need to change
 * @return {Promise<Result<void, HttpError>>}
 */
export async function changeEncryption(question: Question): Promise<Result<void, HttpError>> {
    try {
        const result = await makeSecureApiCall('/misc/changeEncryption', 'POST', {question});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /misc/changeEncryption`, logFormatErr);
            return Err(new HttpError(503, 'Error calling server, returned a null for URL /misc/changeEncryption'));
        }

        const {status, body} = result;

        if (status >= 400) {
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }

        return Ok(undefined);
    }
    catch (err) {
        captureException(err, {extra: {}, tags: ['api']});
        return Err(new HttpError(500, err.message || 'Unknown error changing encryption'));
    }
}

/**
 * Disables a user, and reduces the active Stripe subscription by 1.
 *
 * Sends a revocation message to all logged-in users.
 *
 * @param {uuid} userId
 * @returns {Promise<ChangeSubscriptionResult>}
 */
export async function disableUser(userId: uuid): Promise<Result<ChangeSubscriptionResult, HttpError>> {
    try {
        const result = await makeSecureApiCall('/v2/auth/disableUser', 'POST', {userId});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/disableUser`, logFormatErr);
            return Err(new HttpError(503, 'Error calling server, returned a null for URL /auth/disableUser'));
        }

        const {status, body} = result;

        if (status >= 400) {
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        else return Ok(body);
    }
    catch (err) {
        return Err(new HttpError(500, err.clientMessage || err.message || 'Internal Server Error'));
    }
}

/**
 * Enables a user, and increases the Stripe subscription by 1.
 *
 * @param {uuid} userId
 * @returns {Promise<void>}
 */
export async function enableUser(userId: uuid): Promise<Result<ChangeSubscriptionResult, HttpError>> {
    try {
        const result = await makeSecureApiCall('/v2/auth/enableUser', 'POST', {userId});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/enableUser`, logFormatErr);
            return Err(new HttpError(503, 'Error calling server, returned a null for URL /auth/enableUser'));
        }

        const {status, body} = result;

        if (status >= 400) {
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }
        else return Ok(body);
    }
    catch (err) {
        return Err(new HttpError(500, err.clientMessage || err.message || 'Internal Server Error'));
    }
}

/**
 * Base method to make an authenticated api call.
 *
 * @param {string} path
 * @param {'POST'|'PATCH'|'GET'|'DELETE'|'PUT'} method
 * @param {FormData | any} postArgs
 */
export async function makeSecureApiCall(path: string, method: 'POST' | 'PATCH' | 'GET' | 'DELETE' | 'PUT', postArgs?: FormData | any, signal?: AbortSignal, candidate?: LoginCandidate) {
    // Otherwise, make the call
    return makeApiCall(path, method, postArgs, true, undefined, signal, captureRequestSession(candidate));
}

/** Preserve the account selected before asynchronous hooks and queue processing. */
function makeSessionApiCaller(session: RequestSession) {
    return (path: string, method: 'POST' | 'PATCH' | 'GET' | 'DELETE' | 'PUT', postArgs?: any, signal?: AbortSignal) => {
        session.assertCurrent();
        return makeApiCall(path, method, postArgs, true, undefined, signal, session);
    };
}

function dispatchCurrent(session: RequestSession, action: Parameters<typeof store.dispatch>[0]) {
    session.assertCurrent();
    return store.dispatch(action);
}

async function reportSaveFailure<T extends EntityType>(session: RequestSession, entity: EntityMapper<T>, item: T,
                                                       status: number, message: string): Promise<void> {
    session.assertCurrent();
    if (entity.name === 'document') {
        // Redux may already show a newer version. Mark the exact queued row,
        // which is the source of truth for retry and recovery.
        const result = await idb.update('document', item.addedOn, {
            fatalError: {status, message, recoverable: isRecoverableError(status, message)},
        });
        session.assertCurrent();
        // A user may have discarded this exact row while its request was in flight.
        if (!result.isErr() && result.unwrap() === 0) return;
        if (result.isErr() || result.unwrap() !== 1) {
            console.error('Could not mark the rejected document queue row', result.isErr() ? result.unwrapErr() : item.addedOn);
        }
    }
    dispatchCurrent(session, entity.saveFailure(item, status, message));
}

async function removeForSession(session: RequestSession, entityName: string, key: number | null | undefined) {
    session.assertCurrent();
    // idbd.remove captures its table synchronously; the acknowledgement can
    // retire that row, but never publish into a replacement workspace.
    const result = await idb.remove(entityName, key);
    session.assertCurrent();
    return result;
}

/**
 *
 * @param {string} url - full url to REST api
 * @param {'GET'|'POST'|'DELETE'|'PUT'|'PATCH'} method - http method
 * @param {Record<string, string>} headers - an optional object with header key/value pairs.
 * @param {Record<string, string>} queryParams - optional object with param key/value pairs.
 * @param {any} body - body for the request
 * @returns {Promise<HttpStatusResult>}
 */
async function makeExternalApiCall(url: string, method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH', headers: Record<string, string>, queryParams: Record<string, string>, body: any): Promise<HttpStatusResult> {
    try {
        // Make sure there is a default content type, headers can override
        headers = Object.assign({"Content-Type": "application/json"}, headers);

        if (logging) console.debug(`%c🌐 API: makeExternalApiCall to ${url}`, logFormat);

        let q: string;
        if (queryParams) {
            // Run through the body keys and urlencode each one
            const keyValuePairs: string[] = [];

            Object.keys(queryParams).forEach((key) => {
                const value = queryParams[key];
                let encoded: string;
                if (Array.isArray(value)) encoded = encodeURIComponent(JSON.stringify(value));
                else encoded = encodeURIComponent(value);
                keyValuePairs.push(encodeURIComponent(key) + '=' + encoded);
            });

            q = keyValuePairs.join('&');
            url = url + '?' + q;
        }

        // Make the call to the server
        const response = await fetch(url, {
            method: method,
            headers,
            body,
            mode: 'cors'
        });

        if (logging) console.debug('%c🌐 API: makeExternalApiCall::response %o', logFormat, response);

        // Handle error caused by a null result (like when server is not available)
        if (!response) throw {status: 503, message: 'Server is not available'};

        const result = await getBody(response);
        return {status: response.status, body: result};
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling server, returned a null for URL ${url}`, logFormatErr);
        return {status: 503, body: `Error calling server, returned a null for URL ${url}`};
    }
}

/**
 * Template for calls to API
 * @param {string} path - the path to the API call
 * @param {'POST' | 'PATCH' | 'GET' | 'DELETE' | 'PUT'} method - the method type, e.g. POST, GET, etc.
 * @param {FormData | any} [postArgs] - the body of the call or params to pass
 * @param {boolean} [secure] - use HTTPS?
 * @param {boolean} [inLoop] - are we being called from within makeApiCall?
 * @returns {Promise<HttpStatusResult}
 */
async function makeApiCall(path: string, method: 'POST' | 'PATCH' | 'GET' | 'DELETE' | 'PUT', postArgs?: FormData | any, secure: boolean = false, inLoop?: boolean, signal?: AbortSignal, requestSession?: RequestSession): Promise<HttpStatusResult> {
    if (logging && !postArgs) console.debug(`%c🌐 API: ${secure ? 'https' : 'http'} call to ${path}${inLoop ? 'in loop' : ''}`, logFormat);
    if (logging && postArgs) console.debug(`%c🌐 API: ${secure ? 'https' : 'http'} call to ${path}${inLoop ? 'in loop' : ''} with post args %o`, logFormat, postArgs);

    // Check for connection
    // if (!store.getState().misc.isConnected) {
    //     console.warn('🌐 API: Calling makeApiCall when server is not connected.');
    //     return {status: 503, body: null};
    // }

    const account = secure ? requestSession ?? captureRequestSession() : undefined;
    const credential = account ? await requestServerCredential(account) : undefined;
    account?.assertCurrent();
    if (account) signal = signal ? AbortSignal.any([signal, account.signal]) : account.signal;
    let headers: Record<string, string> = {};
    let contentType: string | null = "application/json";

    if (postArgs && (postArgs instanceof FormData)) {
        contentType = null;
    }

    if (contentType) headers = {
        "Content-Type": contentType
    };

    let b: string | FormData | null = null;
    let q: string;

    const userId = account?.identity.userId ?? getCurrentUserId();
    const socketId = secure ? socket?.connected ? socket.id : null : null;
    const userFragment = (userId) ? `?userId=${userId}` : '';
    const socketFragment = (method !== 'GET' && method !== 'DELETE' && socketId)
                           ? `${userId ? '&' : '?'}socketId=${socketId}`
                           : '';
    let url = `${URI}${path}${userFragment}${socketFragment}`;

    // Get and delete can not process a body, only query params
    if (method !== 'GET' && method !== 'DELETE') {
        if (postArgs) {
            if (postArgs instanceof FormData) b = postArgs;
            else b = JSON.stringify(postArgs);
        }
        else b = null;
    }
    else {
        // Run through the body keys and urlencode each one
        const keyValuePairs: string[] = [];

        Object.keys(postArgs || {}).forEach((key) => {
            const value = postArgs[key];
            let encoded: string;
            if (value !== null && value !== undefined) {
                if (Array.isArray(value)) encoded = encodeURIComponent(JSON.stringify(value));
                else encoded = encodeURIComponent(value);
                keyValuePairs.push(encodeURIComponent(key) + '=' + encoded);
            }
        });

        q = keyValuePairs.join('&');
        if (q) {
            url += `${url.includes('?') ? '&' : '?'}${q}`;
        }
    }

    if (logging) console.debug(`%c🌐 API: makeApiCall ${method} ${url}`, logFormat);

    try {
        // Make the call to the server
        const request: RequestInit = {
            method,
            headers,
            body: b,
            mode: 'cors',
            cache: 'no-store',
            signal,
        };
        let response = account ? await makeSecureRequest(url, request, () => {
            account.assertCurrent();
            return credential!;
        }) : await fetch(url, request);
        account?.assertCurrent();

        if (!response && !inLoop) {
            try {
                // Very likely the server is down, wait a few seconds and call again
                return await pWaitFor(async () => {
                    console.warn(`🌐 API: Retrying ${url}`);
                    account?.assertCurrent();
                    const result = await makeApiCall(path, method, postArgs, secure, true, signal, account);
                    return (result !== null) && pWaitFor.resolveWith(result);
                }, {timeout: 10_000, interval: 1_000, signal: globalAbortSignal});
            }
            catch (err) {
                console.error(`%c🌐 API: Error calling server, returned a null for URL ${url}`, logFormatErr);
                return {status: 503, body: `Error calling server, returned a null for URL ${url}`};
            }
        }

        if (logging) {
            let args = [
                `%c🌐 API: makeApiCall ${method} ${url} response is ${response?.status} (${response.statusText})`,
                !response.status || response?.status >= 400 ? logFormatErr : logFormat
            ];
            if (logging) {
                if (!response || response.status >= 400) console.debug('`%c🌐 API: No response returned or status > 400', logFormatErr, args[0], args[1]);
                else console.debug(`%c🌐 API`, logFormat, args[0], args[1]);
            }
        }

        // Handle rate limiting (429) with Retry-After header
        if (response.status === 429 && !inLoop) {
            const retryAfterHeader = response.headers.get('Retry-After');
            const retryAfterSecs = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 5;
            const retryAfterMs = retryAfterSecs * 1000;

            console.warn(`🌐 API: Rate limited on ${path}. Retrying in ${retryAfterSecs}s`);

            await new Promise(resolve => setTimeout(resolve, retryAfterMs));

            account?.assertCurrent();
            const retryResult = await makeApiCall(path, method, postArgs, secure, true, signal, account);
            account?.assertCurrent();
            if (retryResult.status !== 429) return retryResult;

            // Second attempt also 429'd — give up
            Alert.addSimpleError('Too many requests. Please wait a moment and try again.');
            return retryResult;
        }

        const body = await getBody(response);
        account?.assertCurrent();
        return {status: response.status, body};
    }
    catch (err) {
        account?.assertCurrent();
        if (err instanceof SessionChangedError) throw err;
        console.error(`%c🌐 API: Error in fetch of ${url}`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return {status: 500, body: `Error in fetch of ${url}: ${err?.message}`};
    }
}

/**
 * Get the body of the response based on the response type (JSON, text, etc.)
 *
 * @param {Response} response
 * @returns {Promise<string | any>}
 */
async function getBody(response: Response): Promise<string | any> {
    // Status code is 200 -> 299.  See if there is a body to return
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
        return response.json();
    }
    else if (contentType && contentType.includes("text/plain")) {
        return response.text();
    }
    else {
        return null;
    }
}

/**
 * Check if an email exists.  Checks the MX server and tests the address.
 *
 * @param {uuid} userId
 * @param {string} oldEmail
 * @param {string} newEmail
 * @returns {Promise<User>}
 */
export async function changeEmail(userId: uuid, oldEmail: string, newEmail: string): Promise<User> {
    try {
        const result = await makeSecureApiCall('/auth/changeEmail', 'POST', {userId, oldEmail, newEmail});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/changeEmail`, logFormatErr);
            throw new Error('Error calling server, returned a null for URL /auth/changeEmail');
        }

        const {status, body} = result;

        if (status >= 400) {
            throw new Error(`Error ${status}, ${body.clientMessage || body.message}`);
        }

        return body;
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /auth/changeEmail`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

// from, to, cc, bcc, subject, text, html, replyTo, attachments


/**
 * Send emails from support@outlawpractice.com.
 *
 * @param {EmailOptions} options - the options for the email, e.g. to, from, etc
 * @returns {Promise<SendEmailsResult>}
 */
export async function sendEmails(options: EmailOptions): Promise<Result<SendEmailsResult, Error>> {
    try {
        const operation = captureRequestSession();
        const result = await makeSessionApiCaller(operation)('/misc/sendEmails', 'POST', options);
        operation.assertCurrent();

        if (!result) return Err(new Error(`Error calling server, returned a null for URL /misc/sendEmails`));

        const {status, body} = result;

        // Check to see if any of the recipients were not found
        if (status === StatusCodes.UNPROCESSABLE_ENTITY) {
            // SES email failure
            if (body?.wasEmailProcessed === "No") {
                const errMsg = body.errorMessage || 'Email could not be sent';
                Alert.addSimpleError(errMsg);
                return Err(new Error(errMsg));
            }

            // Fallback for unexpected 422 responses
            const errMsg = 'Error sending email: ' + JSON.stringify(body);
            Alert.addSimpleError(errMsg);
            return Err(new Error(errMsg));
        }
        else if (status === StatusCodes.BAD_REQUEST) { // Bad request, sent bad parameters
            // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request
            Alert.addSimpleError(`Error ${status}: ${body}`);
            return Err(new Error(`Error ${status}: ${body}`));
        }
        else if (status > 400) {
            Alert.addSimpleError(`Error ${status}: ${body?.clientMessage || body?.message || body || getReasonPhrase(status)}`);
            return Err(new Error(`Error ${status}: ${body?.clientMessage || body?.message || body || getReasonPhrase(status)}`));
        }
        else return Ok(body);
    }
    catch (err) {
        if (err instanceof SessionChangedError) return Err(err);
        console.error('%c🌐 API: Error calling /misc/sendEmails', logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api', 'misc', 'sendEmails']});
        return Err(new Error(`API: Error calling /misc/sendEmails ${err.message}`));
    }
}

/**
 * Find a user in Auth0 based on email.
 *
 * @param {string} email - The email to find
 */
export async function findAuth0User(email: string, operation = captureRequestSession()): Promise<Auth0User | null> {
    try {
        const result = await makeSessionApiCaller(operation)('/auth/findUser', 'GET', {email});
        operation.assertCurrent();

        if (!result) throw new Error('🌐 API: Error calling server, returned a null for URL /auth/findUser');

        const {status, body} = result;

        if (status >= 400) {
            // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request
            throw new Error(`🌐 API: Error ${status}, ${body.clientMessage}`);
        }
        else {
            if (body === null) return null;
            else return body.map((u: any) => {
                return {
                    user_id: u.user_id,
                    user_name: u.name,
                    email: u.email,
                    firstName: u.user_metadata.firstName,
                    lastName: u.user_metadata.lastName,
                    firmName: u.user_metadata.firmName,
                    firms: u.app_metadata.firms
                };
            })[0];
        }
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /auth/findUser`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Send a list of firmIds, get back a list of firm objects
 *
 * @param {uuid[]} firmIds - uuids of firms
 * @throws {Error}
 * @returns {Promise<Firm[]>} - An array of Firms
 */
export async function currentFirmInfo(candidate?: LoginCandidate): Promise<{ firm: Firm, logoUrl: string | null }[]> {
    const result = await makeSecureApiCall('/v2/auth/firmInfo', 'GET', {}, undefined, candidate);
    if (!result || result.status >= 400) {
        throw new Error(result?.body?.clientMessage || result?.body?.message || 'Could not verify the authenticated firm');
    }
    return result.body ?? [];
}

/** Legacy public portal lookup; migrate to a signed capability in OWA-844. */
export async function firmInfo(firmIds: uuid[], candidate?: LoginCandidate): Promise<{ firm: Firm, logoUrl: string | null }[]> {
    try {
        const operation = captureRequestSession(candidate);
        // If we did not get an array of firmIds, or the array has an empty item, throw an error
        if (!Array.isArray(firmIds) || firmIds[0] == null) {
            throw new Error('🌐 API: Expecting an array of non-null firms');
        }

        if (!candidate && !store.getState().misc.portalUser) {
            try {
                await pWaitFor(() => store.getState().misc.isConnected, {timeout: 5000, signal: globalAbortSignal});
            }
            catch (err) {
                console.warn(`🌐 API: Error retrieving firm info: ${err.message}`);
            }
        }
        operation.assertCurrent();

        if (!candidate && !store.getState().misc.portalUser && !store.getState().misc.isConnected) {
            // Find another way to return this information so that it doesn't show an error
            const {firmId, userId} = operation.identity;
            if (!Auth.isAuthenticated() || !firmId || !userId) return [];
            const firm = locals.getObject(`${locals.USER_FIRM}-${firmId}-${userId}`) as Firm | undefined;
            // Cached plain JSON carries `firmId`, never the `id` getter (OWA-844).
            if (firm?.firmId === firmId && firmIds.includes(firmId)) return [{firm, logoUrl: null}];

            // Else we have not logged in on the browser before — return an
            // empty array (never undefined; callers handle [] uniformly).
            return [];
        }

        else {
            /**
             * Returns an array of [{firm: Firm, logoUrl: string}]
             */
            const result = await makeSessionApiCaller(operation)('/auth/firmInfo', 'GET', {firmIds: firmIds});
            operation.assertCurrent();

            const body: any = result?.body;
            const status = result?.status || 503;

            if (status >= 400) {
                // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request
                const detail = body?.clientMessage || body?.message || 'Server returned no details';
                if (body) throw new Error(`🌐 API: Error ${status}, ${detail}`);
                else return [];
            }
            else {
                return body ?? [];
            }
        }
    }
    catch (err) {
        console.error(`%c🌐 API: : Error calling /auth/firmInfo`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Public branding for the Client Portal landing page, which renders before
 * sign-in: the firm's legal name and a signed logo URL (OWA-844). Unlike
 * firmInfo, this needs no session and sends no token.
 *
 * @param {uuid} firmId - the firm named in the portal link
 * @throws {Error}
 * @returns {Promise<{legalName: string, logoUrl: string} | null>} - null when the firm is unknown
 */
export async function firmBranding(firmId: uuid): Promise<{ legalName: string, logoUrl: string } | null> {
    try {
        const {status, body} = await makeApiCall(`/auth/firmBranding/${encodeURIComponent(firmId)}`, 'GET');

        if (status === 404) return null;
        if (status >= 400) {
            const detail = body?.clientMessage || body?.message || 'Server returned no details';
            throw new Error(`🌐 API: Error ${status}, ${detail}`);
        }
        return body;
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /auth/firmBranding`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}


/**
 * Find a user in Auth0 based on email.
 *
 * @param {uuid} user_id - The email to find
 * @param {any} fragment
 * @returns {Promise<Auth0User[]>} - Returns an array of Auth0 user structures, or null if none found
 */
export async function updateProfile(user_id: uuid, fragment: any): Promise<Result<GetUsers200ResponseOneOfInner[], Error>> {
    try {
        const result = await makeSecureApiCall('/auth/updateProfile', 'POST', {user_id, fragment});
        if (!result) return Err(new Error('Error calling server, returned a null for URL /auth/updateProfile'));

        const body = result.body;
        const status = result.status || 500;

        if (status >= 400) {
            // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request
            if (body) return Err(new Error(`Call to update profile returned a null, sever probably disconnected mid-stream. ${body.clientMessage || body.message || body}`));
            else {
                console.error(`%c🌐 API: Call to /auth/updateProfile returned a null, sever probably disconnected mid-stream`, logFormatErr);
                // OWA-554: this branch previously fell through to undefined; a Result
                // function must return a Result. Honor the contract with an Err.
                return Err(new Error('Call to /auth/updateProfile failed; server probably disconnected mid-stream'));
            }
        }
        else return Ok(body);
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /auth/updateProfile`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return Err(err);
    }
}

/**
 * Add a user to Auth0
 *
 * @param {Firm} firm
 * @param {User} user
 * @param {Contact} contact
 * @returns {Promise<GetUsers200ResponseOneOfInner>}
 */
export async function addAuth0User(firm: Firm, user: User, contact: Contact): Promise<GetUsers200ResponseOneOfInner> {
    try {
        const result = await makeSecureApiCall('/auth/updateProfile', 'POST', {firm, user, contact});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/updateProfile`, logFormatErr);
            throw new Error('🌐 API: Error calling server, returned a null for URL /auth/updateProfile');
        }

        const {status, body} = result;

        if (status >= 400) {
            // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request
            throw new Error(`🌐 API: Error ${status}, ${body.clientMessage}`);
        }

        return body;
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /auth/addAuth0User`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Delete a user from Auth0 (should never really use)
 *
 * @param {string} email
 * @param {boolean} portalUser
 * @returns {Promise<ChangeSubscriptionResult | void>}
 */
export async function deleteAuth0User(email: string, portalUser: boolean = false): Promise<ChangeSubscriptionResult | void> {
    if (logging) console.debug(`%c🌐 API: deleteAuth0User %s}`, logFormat, email);
    try {
        const operation = captureRequestSession();
        // First we need to get the auth0 user_id from the user's email
        const auth0User = await findAuth0User(email, operation);
        operation.assertCurrent();
        if (!auth0User) {
            console.warn(`🌐 API: Tried to remove Auth0 user, but not found ${email}`);
            Alert.add(new Alert(Alert.ERROR, getZeroUUID(), 0, `Tried to remove Auth0 user, but no email found for ${email}`));
            return;
        }

        if (logging) console.debug('%c🌐 API: deleteAuth0User::auth0Users %o', logFormat, auth0User);

        // This either returns the result of changeSubscriptionAmount or void in the body
        const result = await makeSessionApiCaller(operation)(
            `/auth/deleteUser/${auth0User.user_id}`,
            'DELETE',
            {portalUser: portalUser}
        );
        operation.assertCurrent();

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/deleteUser/${auth0User.user_id}`, logFormatErr);
            throw new Error(`🌐 API: Error calling server, returned a null for URL /auth/deleteUser/${auth0User.user_id}`);
        }

        const {status, body} = result;

        if (status >= 400) {
            // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request
            throw new Error(`🌐 API: Error ${status}, ${body.clientMessage}`);
        }

        if (status === StatusCodes.NO_CONTENT) return;
        else return body as ChangeSubscriptionResult;
    }
    catch (err) {
        console.error(`%c🌐 API:  Error calling /auth/deleteUser`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Add a user to Auth0
 *
 * @param {Contact} contact
 * @returns {Promise<void>}
 */
export async function addPortalUser(contact: Contact): Promise<void> {
    try {
        const result = await makeSecureApiCall('/auth/createPortalUser', 'POST', {contactId: contact.id});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /auth/createPortalUser`, logFormatErr);
            throw new Error('🌐 API: Error calling server, returned a null for URL /auth/createPortalUser');
        }

        const {status, body} = result;

        if (status >= 400) {
            // The more likely error is one from Auth0, which will usually just be error 400 - Bad Request
            throw new Error(`🌐 API: Error ${status}, ${body.clientMessage}`);
        }
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /auth/createPortalUser`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 *
 * @param {Document} doc - The document to create. Pass large files using doc.fileHandle, not buffer.
 * @param {boolean} _saveContents - if true, we need to create a new S3 document. If false, we just need to do a name change
 * @returns {Promise<Document>}
 */
export async function createRemoteDocument(doc: Document, _saveContents: boolean, operation = captureRequestSession()): Promise<Document> {
    operation.assertCurrent();
    const boundApiCall = makeSessionApiCaller(operation);
    if (logging) console.debug('🌐 API: createRemoteDocument', doc);

    // TODO: Need to discern a name change from a new document create.
    //       Suggest having a separate api call to /v2/misc/renameDocument.
    try {
        const sendsFile = !!(doc.fileHandle?.name && doc.fileName);

        // OWA-1149: the last guard. A file past its limit (one queued before the limit, or by a
        // path that did not check) is refused as the server would refuse it, without sending it;
        // the 413 holds the queued upload as a server's 413 does.
        const refusal = sendsFile ? uploadSizeRefusal(doc.fileName, doc.fileHandle.size, doc.contentType) : null;
        if (refusal) throw new HttpError(413, refusal);

        // The document goes first, so the server knows the file's class while the file streams.
        const form = new FormData();
        form.append('doc', JSON.stringify(doc));
        if (sendsFile) form.set('file', doc.fileHandle, doc.fileName);

        // Send the document, along with the html as an attachment or inline
        const result = await boundApiCall(`/v2/misc/createDocument`, 'POST', form);
        operation.assertCurrent();

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /misc/createDocument`, logFormatErr);
            throw new HttpError(503, 'Error calling server, returned a null for URL /v2/misc/createDocument');
        }

        const {status, body} = result;

        if (logging) console.debug(`API: createRemoteDocument result with status of ${status}`, body);

        if (status >= 400) {
            console.error(`%c🌐 API: createRemoteDocument returned status ${status}, ${body?.clientMessage || body?.message || JSON.stringify(body)}`, logFormatErr);
            throw new HttpError(status, (typeof body === 'string' && body) || body?.clientMessage || body?.message || `Document upload failed (${status})`);
        }

        return new Document(body);
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /v2/misc/createDocument`, logFormatErr, err);
        // A 4xx is a handled server rejection; only server failures and unexpected errors belong in exception telemetry.
        if (!(err instanceof HttpError && err.status < 500)) captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 *
 * @param {Document[]} docs
 * @return {Promise<Document[]>}
 */
export async function signDocuments(docs: Document[]): Promise<Document[]> {
    return refreshDocumentSignatures(docs, requestDocumentSignatures);
}

async function requestDocumentSignatures(docs: Document[]): Promise<Document[]> {
    try {
        if (logging) console.debug('🌐 API: signDocuments', docs);

        // Send the document, along with the html as an attachment or inline
        const result = await makeSecureApiCall(`/misc/signDocuments`, 'POST', {documents: docs});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /misc/signDocuments`, logFormatErr);
            throw new Error('🌐 API: error signing documents, returned a null for URL /misc/signDocuments');
        }

        const {status, body} = result;

        // If the server is down, don't worry about signing.
        if (status === 503) return docs;

        if (status >= 400) {
            // The more likely error is one from the S3 api, which can be a number of values
            throw new Error(`🌐 API: Error ${status}, ${body?.clientMessage || body?.message || body}`);
        }

        // In this case, the body should be an array of documents, (not Document objects)
        if (Array.isArray(body)) {
            return body.map(d => new Document(d));
        }
        else {
            if (!body) throw new Error(`🌐 API: body was ${body}, HTTP status was ${status}`);
            else throw new Error(`🌐 API: expecting body to be an array`);
        }
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /misc/signDocuments`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Get all previous versions of the given entity, with full dependencies.
 *
 * @param {EntityNames} entityType
 * @param {EntityType} entity
 * @returns {Promise<<T[]>>}
 */
export async function getEntityHistory<T extends EntityType>(entityType: EntityNames, entity: T): Promise<T[]> {
    try {
        const result = await makeSecureApiCall(`/${entityType}/versions/` + entity.id, 'GET', {
            asof: '19700101'
        });

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /${entityType}/versions/${entity.id}`, logFormatErr);
            throw new Error(`🌐 API: Error calling server, returned a null for URL /${entityType}/versions/${entity.id}`);
        }

        const {status, body} = result;

        if (status >= 400) {
            // Can't imagine getting an error here.
            throw new Error(`🌐 API: Error ${status}, ${body.clientMessage || body.message}`);
        }

        return body;
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /${entityType}/history/${entity.id}`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        throw err;
    }
}

/**
 * Get all previous versions of the given entity.
 */
export async function getEntityVersions<T>(entityType: EntityNames, entity: EntityType): Promise<Result<T[], HttpError>> {
    try {
        const result = await makeSecureApiCall(`/${entityType}/versions/` + entity.id, 'GET', {
            asof: '19700101'
        });

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /${entityType}/versions/${entity.id}`, logFormatErr);
            return Err(new HttpError(503, `Server is not accessible, try again in a few moments /${entityType}/versions/${entity.id}`));
        }

        const {status, body} = result;

        if (status >= 400) {
            // Can't imagine getting an error here.
            return Err(new HttpError(status, httpErrorMessage(status, body)));
        }

        return Ok(body);
    }
    catch (err: any) {
        console.error(`%c🌐 API: Error calling /${entityType}/history/${entity.id}`, logFormatErr, err);
        return Err(err);
    }
}

/**
 * Return the client and server versions
 *
 * Returns Err 503 without a request while the server connection is down.
 * @returns {Promise<VersionInfo>}
 */
export async function getVersions(): Promise<Result<VersionInfo, HttpStatusResult>> {
    try {
        if (!store.getState().misc.isConnected) {
            return Err({status: 503, body: 'Server version is unavailable while offline.'});
        }

        // Get the client and server versions
        const result = await makeApiCall(`/misc/versions`, 'GET', {});

        if (!result) {
            console.error(`%c🌐 API: Error calling server, returned a null for URL /misc/versions`, logFormatErr);
            return Err({status: 503, body: `Error calling server, returned a null for URL /misc/versions`});
        }

        const {status, body} = result;

        if (status >= 400) {
            // Can't imagine getting an error here.
            return Err({status, body: body.clientMessage});
        }

        return Ok(body);
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /misc/versions`, logFormatErr, err);
        captureException(err, {extra: {}, tags: ['api']});
        return Err({status: 500, body: err.clientMessage || err.message});
    }
}

/**
 * Export IOLTA Balance Report as Excel file via server-side generation
 * Server generates the Excel with full styling using exceljs, returns base64-encoded data
 *
 * @param year - The fiscal year
 * @param yearData - The year's financial data (months, totals, etc.)
 * @returns true if successful, false otherwise
 */
export async function exportIoltaBalanceReport(year: number, yearData: any): Promise<boolean> {
    try {
        if (!store.getState().misc.isConnected) {
            return false;
        }

        const result = await makeSecureApiCall('/misc/iolta-balance-report', 'POST', {year, yearData});

        if (!result || result.status >= 400) {
            console.error(`%c🌐 API: Error generating IOLTA report`, logFormatErr, result);
            return false;
        }

        const {filename, contentType, data} = result.body;

        // Decode base64 and create blob
        const binaryString = atob(data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {type: contentType});

        // Trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        return true;
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /misc/iolta-balance-report`, logFormatErr, err);
        return false;
    }
}

/**
 * Export Trust Ledger by Client report to Excel (server-side with full styling)
 *
 * @param clientName - The client's display name
 * @param ledgerData - Array of ledger rows with transactions
 * @param summary - Summary totals (opening, receipts, disbursements, closing)
 * @param dateRange - Optional date range for the report
 * @returns true if successful, false otherwise
 */
export async function exportTrustLedgerReport(
    clientName: string,
    ledgerData: any[],
    summary: any,
    dateRange?: {startDate: Date | null; endDate: Date | null} | null
): Promise<boolean> {
    try {
        if (!store.getState().misc.isConnected) {
            return false;
        }

        const result = await makeSecureApiCall('/misc/trust-ledger-report', 'POST', {
            clientName,
            ledgerData,
            summary,
            dateRange,
        });

        if (!result || result.status >= 400) {
            console.error(`%c🌐 API: Error generating Trust Ledger report`, logFormatErr, result);
            return false;
        }

        const {filename, contentType, data} = result.body;

        // Decode base64 and create blob
        const binaryString = atob(data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {type: contentType});

        // Trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        return true;
    }
    catch (err) {
        console.error(`%c🌐 API: Error calling /misc/trust-ledger-report`, logFormatErr, err);
        return false;
    }
}

// ==========================================
// Accounting Integration APIs (Xero/QBO)
// ==========================================

/**
 * Accounting connection status response
 */
export type AccountingConnectionStatus = {
    connected: boolean;
    provider: 'xero' | 'qbo' | null;
    providerDisplayName: string | null;
    status: 'connected' | 'disconnected' | 'error';
    errorMessage: string | null;
    lastBalanceFetchAt: string | null;
};

/**
 * External bank account from Xero/QBO
 */
export type ExternalBankAccount = {
    id: string;
    name: string;
    type: 'bank' | 'checking' | 'savings' | 'trust' | 'other';
    balance?: number;
    currency?: string;
};

/**
 * Account balance from external provider
 */
export type ExternalAccountBalance = {
    balance: number;
    currency: string;
    asOf: string;
    accountId: string;
};

/**
 * Get the current accounting connection status for the firm
 */
export async function getAccountingConnection(): Promise<AccountingConnectionStatus> {
    const result = await makeSecureApiCall('/accounting/connection', 'GET', {});

    if (!result) {
        throw new Error('Failed to fetch accounting connection status');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to fetch accounting connection status');
    }

    return result.body;
}

/**
 * Initiate OAuth connection to an accounting provider
 * Returns the authorization URL to redirect the user to
 */
export async function connectAccounting(provider: 'xero' | 'qbo'): Promise<{authUrl: string}> {
    const result = await makeSecureApiCall(`/accounting/connect/${provider}`, 'POST', {});

    if (!result) {
        throw new Error('Failed to initiate accounting connection: no response');
    }

    if (result.status >= 400) {
        console.error('connectAccounting error:', result.status, result.body);
        throw new Error(result.body?.message || `Failed to initiate accounting connection (${result.status})`);
    }

    return result.body;
}

/**
 * Disconnect from the accounting provider
 */
export async function disconnectAccounting(): Promise<void> {
    const result = await makeSecureApiCall('/accounting/connection', 'DELETE', {});

    if (!result) {
        throw new Error('Failed to disconnect from accounting');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to disconnect from accounting');
    }
}

/**
 * Get list of bank accounts from the connected accounting software
 */
export async function getExternalAccounts(): Promise<{accounts: ExternalBankAccount[]}> {
    const result = await makeSecureApiCall('/accounting/accounts', 'GET', {});

    if (!result) {
        throw new Error('Failed to fetch external accounts');
    }

    if (result.status === 401 && result.body?.reconnectRequired) {
        const error: any = new Error(result.body?.message || 'Connection expired');
        error.reconnectRequired = true;
        throw error;
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to fetch external accounts');
    }

    return result.body;
}

/**
 * Get the current balance for an external account
 */
export async function getExternalBalance(externalAccountId: string): Promise<ExternalAccountBalance> {
    const result = await makeSecureApiCall(`/accounting/balance/${externalAccountId}`, 'GET', {});

    if (!result) {
        throw new Error('Failed to fetch account balance');
    }

    if (result.status === 401 && result.body?.reconnectRequired) {
        const error: any = new Error(result.body?.message || 'Connection expired');
        error.reconnectRequired = true;
        throw error;
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to fetch account balance');
    }

    return result.body;
}

/**
 * Map an Outlaw bank account to an external account
 */
export async function mapBankAccount(
    bankAccountId: string,
    externalAccountId: string | null,
    externalAccountName: string | null
): Promise<void> {
    const result = await makeSecureApiCall(`/accounting/map/${bankAccountId}`, 'POST', {
        externalAccountId,
        externalAccountName,
    });

    if (!result) {
        throw new Error('Failed to update account mapping');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to update account mapping');
    }
}

/**
 * Fetch a historical pipeline snapshot (leads + milestones as of a date).
 * Uses the existing findMany endpoints with an addedOn <= asOf exemplar.
 *
 * @param {number} asOf - HRT timestamp (microseconds) representing the cutoff date
 */
export async function getPipelineSnapshot(asOf: number): Promise<{ leads: any[]; milestones: any[] }> {
    const options = JSON.stringify({addedOn: {$lte: asOf}, status: {$ne: 'D'}});

    const [leadResult, milestoneResult] = await Promise.all([
        makeSecureApiCall('/lead', 'GET', {options}),
        makeSecureApiCall('/milestone', 'GET', {options}),
    ]);

    return {
        leads: leadResult?.body ?? [],
        milestones: milestoneResult?.body ?? [],
    };
}

/**
 * Upload a user avatar image. The server stores it in S3 and updates user.picture
 * with a lightweight flag instead of the full base64 data.
 *
 * @param {string} imageDataUrl - base64 data URL (e.g. "data:image/jpeg;base64,...")
 * @returns {Promise<{picture: string}>} - the new picture flag value
 */
export async function uploadAvatar(imageDataUrl: string): Promise<{picture: string}> {
    const result = await makeSecureApiCall('/auth/avatar', 'POST', {image: imageDataUrl});

    if (!result || result.status >= 400) {
        throw new Error(`Failed to upload avatar: ${result?.body?.message || 'unknown error'}`);
    }

    return result.body;
}

// ==========================================
// Gusto Integration APIs
// ==========================================

export type GustoConnectionStatus = {
    configured?: boolean;
    connected: boolean;
    provider: 'gusto';
    status: 'connected' | 'disconnected' | 'error';
    errorMessage: string | null;
    companyUuid?: string;
};

export type GustoEmployeeMatch = {
    matched: Array<{
        gustoUuid: string;
        gustoEmail: string;
        gustoName: string;
        outlawUserId: string;
        outlawEmail: string;
        matchType: 'email' | 'manual';
    }>;
    unmatched: Array<{gustoUuid: string; gustoEmail: string; gustoName: string}>;
};

export type GustoSyncResult = {
    synced: number;
    skipped: number;
    errors: Array<{employee: string; error: string}>;
};

/**
 * Get the current Gusto connection status for the firm
 */
export async function getGustoConnection(): Promise<GustoConnectionStatus> {
    const result = await makeSecureApiCall('/gusto/connection', 'GET', {});

    if (!result) {
        throw new Error('Failed to fetch Gusto connection status');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to fetch Gusto connection status');
    }

    return result.body;
}

/**
 * Initiate OAuth connection to Gusto.
 * Returns the authorization URL to redirect the user to.
 */
export async function connectGusto(useSandbox?: boolean): Promise<{authUrl: string}> {
    const result = await makeSecureApiCall('/gusto/connect', 'POST', {useSandbox});

    if (!result) {
        throw new Error('Failed to initiate Gusto connection: no response');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || `Failed to initiate Gusto connection (${result.status})`);
    }

    return result.body;
}

/**
 * Disconnect from Gusto
 */
export async function disconnectGusto(): Promise<void> {
    const result = await makeSecureApiCall('/gusto/connection', 'DELETE', {});

    if (!result) {
        throw new Error('Failed to disconnect from Gusto');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to disconnect from Gusto');
    }
}

/**
 * Preview employee email matching between Gusto and Outlaw users
 */
export async function getGustoEmployees(): Promise<GustoEmployeeMatch> {
    const result = await makeSecureApiCall('/gusto/employees', 'GET', {});

    if (!result) {
        throw new Error('Failed to fetch Gusto employees');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to fetch Gusto employees');
    }

    return result.body;
}

/**
 * Trigger time-off sync from Gusto
 */
export async function syncGusto(): Promise<GustoSyncResult> {
    const result = await makeSecureApiCall('/gusto/sync', 'POST', {});

    if (!result) {
        throw new Error('Failed to sync Gusto time-off');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to sync Gusto time-off');
    }

    return result.body;
}

export type GustoHolidayImportResult = {
    imported: number;
    skipped: number;
};

/**
 * Import paid holidays from Gusto into the firm's holiday calendar
 */
export async function importGustoHolidays(): Promise<GustoHolidayImportResult> {
    const result = await makeSecureApiCall('/gusto/holidays/import', 'POST', {});

    if (!result) {
        throw new Error('Failed to import Gusto holidays');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to import Gusto holidays');
    }

    return result.body;
}

/**
 * Save manual Gusto employee-to-Outlaw user mappings
 */
export async function saveGustoMappings(mappings: Array<{gustoUuid: string; outlawUserId: string}>): Promise<void> {
    const result = await makeSecureApiCall('/gusto/employees/mappings', 'PUT', {mappings});

    if (!result) {
        throw new Error('Failed to save Gusto employee mappings');
    }

    if (result.status >= 400) {
        throw new Error(result.body?.message || 'Failed to save Gusto employee mappings');
    }
}

/**
 * Get the URL for a user's avatar image, served through the server proxy.
 * Append a cache-busting timestamp from the picture flag if available.
 *
 * @param {string} userId
 * @param {string|null} pictureFlag - the user.picture value (e.g. "avatar:1708200000")
 * @returns {string|null} - URL to fetch the avatar, or null if no avatar
 */
export function getAvatarUrl(userId: string, pictureFlag?: string | null): string | null {
    if (!pictureFlag) return null;

    // Legacy: if picture is a full URL (e.g. from Auth0 gravatar), return as-is
    if (pictureFlag.startsWith('http')) return pictureFlag;

    // "avatar:1708200000" → extract timestamp for cache busting
    const ts = pictureFlag.startsWith('avatar:') ? pictureFlag.split(':')[1] : '';
    const firmId = getCurrentFirmId();
    return `${URI}/auth/avatar/${userId}?firmId=${firmId}&t=${ts}`;
}
